/**
 * **Ten, in two groups.**
 *
 * The **five appointment shapes** come first — `one-room`, `the-practice`,
 * `the-studio`, `the-quiet-spa`, `the-venue` — and they are §9.5's five home
 * shapes, each at seven bands, each cut from the survey of thirty-one real
 * appointment-business homepages and seventeen template demos
 * (`docs/design/references/homepage-sequences.md`). The order is the order
 * §9.5's table reads, which is roughly the order a picker should offer them:
 * the default spa first, then the two that fit a named client, then the two
 * that are a register and an option rather than a vertical.
 *
 * The **five vertical templates** follow — `the-table`, `stays`,
 * `departures`, `the-shelf`, `resort` — and they are §8 of
 * `docs/design/references/homepage-sequences-verticals.md`, each at seven
 * bands, each cut from its own vertical's survey rather than from the spa's.
 * Re-cutting a restaurant home from spa evidence would have been the taste
 * argument this whole section exists to replace, which is why they waited;
 * what they waited for was forty-four live sites, and each template's comment
 * names the rows that decided its bands.
 *
 * The names of the four new ones are descriptive and the owner may rename
 * them; each doc comment records what the name is trying to say, so a rename
 * is a rename and not a re-argument.
 *
 * **Then one that is not a business shape at all.** `the-platform` is
 * eleventh and last: the site that sells this platform to operators, built
 * against our own product site — **clearDay**, the brand JSWaves sells under
 * (`docs/clients.md`, client 1, "none yet"). It is
 * exempt from three of the rules the ten share — seven bands, the story at
 * band 2 or 3, and the `CLOSES` table — and the exemption is a named constant
 * with its reason attached (`PRODUCT_TEMPLATES` in `./theme.test.ts`), not a
 * `filter` on something incidental. The reason is the layer: the three
 * homepage surveys read **tenant** sites selling to guests, and a product site
 * sells software to the owner of one. Its evidence is
 * `docs/design/references/timekey.md` — a competitor's platform home read band
 * by band — and the platform's own screenshots.
 */
export const TEMPLATE_NAMES = [
    'one-room',
    'the-practice',
    'the-studio',
    'the-quiet-spa',
    'the-venue',
    'the-table',
    'stays',
    'departures',
    'the-shelf',
    'resort',
    // **Eleventh, and last, because it is not a business shape.** The ten above
    // are verticals a tenant picks from; `the-platform` is the site that sells
    // the platform *to* those tenants, and the picker should offer it after
    // everything a booking business would ever choose. Its evidence, its band
    // count and its closing band are its own — see `PRODUCT_TEMPLATES` in
    // `theme.test.ts`.
    'the-platform',
];
export function isTemplateName(name) {
    return TEMPLATE_NAMES.includes(name);
}
/* ------------------------------------------------------------------ */
/* Placeholders                                                        */
/* ------------------------------------------------------------------ */
/**
 * A picture-shaped placeholder: an inline SVG at the ratio the shopping list
 * asks for, with the ratio written on it, so a template previews as a page
 * with photographs *in the right places* rather than as a page with holes —
 * and so a slice, a person or a quote can carry the image the contract
 * requires before the tenant's own arrives. Replaced, never published: the
 * shopping list is the checklist of every one of these.
 */
export function placeholderImage(ratio, label = ratio) {
    const [w, h] = ratio.split(':').map(Number);
    const width = 400;
    const height = Math.round((width * h) / w);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${String(width)}" height="${String(height)}" viewBox="0 0 ${String(width)} ${String(height)}">` +
        `<rect width="100%" height="100%" fill="#d9d4cc"/>` +
        `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="28" fill="#6b655c">${label}</text>` +
        `</svg>`;
    return `data:image/svg+xml;base64,${toBase64(svg)}`;
}
/**
 * UTF-8 → base64, and the "UTF-8" is the whole point.
 *
 * `btoa` is **Latin-1 only**: it throws `InvalidCharacterError` on any code
 * point above U+00FF. A label is prose, and the prose in this repo uses an em
 * dash — *"Shot 1 — the shelf wall"* — so `placeholderImage` used to throw from
 * inside a pure function for the most ordinary punctuation there is, and the
 * one spike that hit it worked around the encoder by choosing its bullet
 * (`spikes/lovespa`, `brief.md` §8 finding 7).
 *
 * So the text is encoded to bytes first and `btoa` is handed the one-byte-per-
 * character string it actually takes.
 *
 * `TextEncoder` and `btoa`, and **nothing from Node**: this package is imported
 * by the browser bundle, and dev.md §2 fixes the runtime at Node 24, where both
 * are globals. A `Buffer` fallback used to sit here for "older runtimes"; there
 * are none, and a branch nothing reaches is a branch nothing tests
 * (`docs/guest-site-plan.md` D4).
 */
function toBase64(text) {
    const bytes = new TextEncoder().encode(text);
    let latin1 = '';
    for (const byte of bytes)
        latin1 += String.fromCharCode(byte);
    return btoa(latin1);
}
const photo = placeholderImage;
/* ------------------------------------------------------------------ */
/* The ten                                                             */
/* ------------------------------------------------------------------ */
/**
 * **One Room** — the spa shape, re-cut (`docs/site-section-system.md` §9.5,
 * phase 8); built against Love Spa Frenchtown, whose page is this arrangement
 * plus a handful of rules of identity.
 *
 * **Ten bands became seven.** §4.3's One Room was written before the survey
 * and is superseded by §9.5's spa row: hero cover → story split → services
 * cards → testimonials cards → gallery grid → location → cta. What the
 * evidence took out was `team` (52 % of real sites, but the room's one person
 * belongs in the story's picture on a page this short), `features` (39 %, and
 * "spas rarely" — `docs/design/references/homepage-sequences.md` §5) and the
 * FAQ band, which phase 7 had already moved to `/p/faq`. What it moved was
 * `story`, up to band 2: 74 % of real sites carry one and its median place is
 * band 2 or 3, which is the single biggest correction the survey made.
 *
 * **What "seven" counts.** Seven *bands between the masthead and the footer*
 * — the survey's own unit (§5: "masthead and footer excluded (always
 * present)"). So the masthead (a branding key, not a block), the `footer`,
 * and the pinned `booking` block are outside the count. Booking is outside it
 * for a reason of its own and not as an accounting convenience: §9.4 says
 * plainly that the booking control is not one of the seven (2 of 31 real
 * sites), and `PINNED_SECTION` says the bundle mounts the flow whether or not
 * the block is arranged. So the block is *placed* rather than chosen — at the
 * foot, under the ask, which is where a guest who has just pressed the cta's
 * "Book a time" lands — and it is not one of the seven any more than the
 * masthead is.
 *
 * **Its content pages.** `faq` stayed where phase 7 put it. `story` is new
 * and is the reason the home's story band could shrink to a paragraph: the
 * split on the home is the short version a stranger reads standing up, the
 * page under `link_target: 'page:story'` is the long one.
 */
const ONE_ROOM = {
    name: 'one-room',
    label: 'One Room',
    vertical: 'A one- or two-person spa or salon',
    client: 'Love Spa Frenchtown',
    blurb: 'The room in a photograph, a paragraph beside your portrait, the menu as cards with a Book on every row, what guests said, the place, the way there, and one last ask.',
    document: {
        preset: 'serene',
        tokens: { 'kicker.mark': 'dot', 'kicker.color': 'accent-2', 'radius.button': '999px' },
        tagline: 'One room, one pair of hands.',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your street · Your town',
                    headline: 'Your promise, in one line',
                    headline_accent: 'one line',
                    sub: 'Two sentences about the room, the hands, and how to book — the way you would say it at the door.',
                    cta_label: 'Book a time',
                    cta_target: 'section:booking',
                    variant: 'cover',
                    scrim: 'dusk',
                    accent_style: 'italic',
                },
            },
            {
                type: 'story',
                id: 'the-room',
                slots: {
                    kicker: '01 · The room',
                    headline: 'One room, one pair of hands',
                    body: 'Three or four sentences: who you are, how long you have done this, what a first visit is like. This is the paragraph a stranger reads standing up — the long version has a page of its own.',
                    image: photo('4:5', 'portrait 4:5'),
                    variant: 'split',
                    link_label: 'Read the longer story',
                    link_target: 'page:story',
                },
            },
            {
                type: 'services',
                id: 'menu',
                slots: {
                    kicker: '02 · The menu',
                    headline: 'Every treatment, priced plainly',
                    note: 'What the price includes, in one sentence.',
                    variant: 'cards',
                    columns: '2',
                    book_verb: true,
                    show_price: true,
                    show_duration: true,
                },
            },
            {
                type: 'testimonials',
                id: 'guests',
                slots: {
                    tone: 'alt',
                    kicker: '03 · Guests',
                    headline: 'What guests say',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A guest’s name',
                            text: 'Their words, as they wrote them.',
                            bullets: ['The treatment · with you'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'Another name',
                            text: 'Their words.',
                            bullets: ['The treatment'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A third',
                            text: 'Their words.',
                            bullets: ['The treatment'],
                        },
                    ],
                },
            },
            {
                type: 'gallery',
                id: 'the-place',
                slots: {
                    kicker: '04 · The place',
                    headline: 'Where you will be',
                    variant: 'grid',
                    columns: '3',
                    images: [
                        { src: photo('3:2', 'place 3:2'), alt: 'The room', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'The entrance', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'A detail', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'The street', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'The view', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'The shelf', caption: '' },
                    ],
                },
            },
            {
                type: 'location',
                id: 'find-us',
                slots: {
                    kicker: '05 · The door',
                    headline: 'Find us',
                    address: 'Street and number\nTown, island, postcode',
                    phone: '+1 (340) 555-0100',
                    map: photo('4:3', 'map 4:3'),
                    show_hours: true,
                    directions: true,
                },
            },
            {
                type: 'cta',
                id: 'book',
                slots: {
                    tone: 'accent',
                    kicker: '06 · Book',
                    headline: 'Come and be looked after',
                    note: 'Pick a treatment and a day. A text confirms it.',
                    link_label: 'Book a time',
                    link_target: 'section:booking',
                },
            },
            /*
             * The pinned mount, not one of the seven (§9.4, and `PINNED_SECTION`):
             * the bundle offers the flow whether or not this block is arranged, so
             * the only thing the document decides is *where* it appears. At the
             * foot, under the ask, is where the cta's button sends a guest.
             */
            {
                /*
                 * **`card` — the owner's preference, and the shape a spa's day is
                 * actually in** (amended 2026-09-18, §4.1's When row).
                 *
                 * The study chose the week strip from Fresha and Booksy and recorded
                 * in the same row that Cal.com and Calendly show the month itself; we
                 * shipped one of the two for everybody. The card is not the sparse
                 * schedule's shape *instead* of the dense one's — it carries **both**:
                 * the month with a mark on every day, and, past eight openings, the
                 * Morning · Afternoon · Evening segments the strip's day parts were
                 * for. A spa with a full Tuesday gets the parts; a spa with three
                 * openings next week gets a month that shows them without paging.
                 */
                type: 'booking',
                id: 'booking',
                slots: {
                    headline: 'Book a time',
                    note: 'Pick a treatment and a day. A text confirms it.',
                    variant: 'card',
                },
            },
            {
                type: 'footer',
                id: 'footer',
                slots: {
                    tone: 'night',
                    note: 'Your name — one room, one pair of hands, since …',
                },
            },
        ],
        pages: {
            story: {
                title: 'Our story',
                sections: [
                    {
                        type: 'story',
                        id: 'story',
                        slots: {
                            kicker: 'Since …',
                            headline: 'The longer story',
                            body: 'The paragraphs that do not fit on the home page: how the room came to be, who taught you, what changed along the way, and what you want a first-time guest to know before they lie down.',
                            image: photo('3:2', 'room 3:2'),
                            variant: 'editorial',
                        },
                    },
                ],
            },
            faq: {
                title: 'Questions',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            tone: 'alt',
                            kicker: 'Good questions',
                            headline: 'Good questions',
                            variant: 'pills',
                            items: [
                                { q: 'Where do I park?', a: 'Your answer.' },
                                { q: 'What should I wear?', a: 'Your answer.' },
                                { q: 'Can I book for two?', a: 'Your answer.' },
                                { q: 'What if I need to cancel?', a: 'Your policy, plainly.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Treatments', target: 'section:menu' },
            { label: 'Our story', target: 'page:story' },
            { label: 'The place', target: 'section:the-place' },
            { label: 'Questions', target: 'page:faq' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        { where: 'hero', ratio: '16:9', count: 1, note: 'The room, wide, in its best light.' },
        {
            where: 'story',
            ratio: '4:5',
            count: 1,
            note: 'A portrait of you beside the paragraph — at work, or at the door.',
        },
        {
            where: 'story',
            ratio: '3:2',
            count: 1,
            note: 'One more of the room, for the story page.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 3,
            note: 'A small portrait per guest, or leave them out.',
        },
        {
            where: 'gallery',
            ratio: '3:2',
            count: 6,
            note: 'The place, three abreast: entrance, room, a detail, the street, the view, the shelf.',
        },
        { where: 'location', ratio: '4:3', count: 1, note: 'A still map or an aerial photograph.' },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **The Practice** — the person-led shape (§9.5 row 2); built against Terry
 * Stevens, and the answer to the open question in `docs/clients.md` ("whether
 * a one-person business's home page *is* the person page"): it is, and this
 * is what that looks like without becoming a CV.
 *
 * hero split (the portrait beside the first sentence) → story → team one →
 * services list → testimonials single → location → cta. The survey's evidence
 * is unusually clean here: the therapist, the tattoo artist and both
 * estheticians all run hero → story/about → services → one quote → cta, and
 * `team` is a home band on 52 % of real sites against 18 % of template demos
 * — the one place the marketplaces under-serve what small businesses actually
 * build.
 *
 * **Why the portrait appears twice.** The hero's is the face, at the size a
 * stranger decides on; the `team: one` band's is the credentials, beside the
 * letters and the years. They are two different jobs and the shopping list
 * asks for two frames, not one photograph used twice.
 *
 * **No booking block.** §9.6's verdict on this shape drops the inline control
 * from the default — the home links to the item page, where the flow lives —
 * so the hero's button and the cta's both point at the services band, whose
 * rows carry the Book verb.
 *
 * **The preset is `gilded`.** A warm cream ground and an old-style serif read
 * like a nameplate, which is what a page whose headline is a person's name
 * is. `serene` would have been One Room again in a second colour.
 *
 * The name is descriptive and the owner may rename it; what it has to keep is
 * the shape, which is the survey's, not the name's.
 */
const THE_PRACTICE = {
    name: 'the-practice',
    label: 'The Practice',
    vertical: 'One practitioner and the work they do',
    client: 'Terry Stevens',
    blurb: 'Your face beside your first sentence, how you work, what you trained in, the sessions as rows with a Book on each, one person’s words, the way there, and the ask.',
    document: {
        preset: 'gilded',
        tagline: 'One practitioner. The work, and the person who does it.',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your town',
                    headline: 'Your name, and what you do',
                    sub: 'One sentence about the work and who it is for, then one about how to start.',
                    cta_label: 'Book a session',
                    cta_target: 'section:sessions',
                    variant: 'split',
                    align: 'left',
                    image: photo('4:5', 'portrait 4:5'),
                },
            },
            {
                type: 'story',
                id: 'how-i-work',
                slots: {
                    kicker: '01 · How I work',
                    headline: 'How I work',
                    body: 'Two or three sentences on what a session is actually like — what you ask at the start, what you do with the answer, what people notice afterwards. Written the way you would say it, not the way a brochure would.',
                    variant: 'editorial',
                    link_label: 'More about me',
                    link_target: 'page:story',
                },
            },
            {
                type: 'team',
                id: 'credentials',
                slots: {
                    tone: 'alt',
                    kicker: '02 · Training',
                    headline: 'Who you will be working with',
                    variant: 'one',
                    items: [
                        {
                            image: photo('4:5', 'portrait 4:5'),
                            title: 'Your name, and the letters after it',
                            text: 'A paragraph: where you trained, how long you have practised, what you keep in your notes about each person, and anything a first-time client should know before they arrive.',
                            bullets: ['Licensed in …', 'Trained at …', 'Practising since …', 'Speaks …'],
                        },
                    ],
                },
            },
            {
                type: 'services',
                id: 'sessions',
                slots: {
                    kicker: '03 · Sessions',
                    headline: 'What a session is, and what it costs',
                    note: 'One line on what the price includes and how long to allow.',
                    variant: 'list',
                    book_verb: true,
                    show_price: true,
                    show_duration: true,
                },
            },
            {
                type: 'testimonials',
                id: 'in-their-words',
                slots: {
                    kicker: '04 · In their words',
                    headline: 'One person’s words',
                    variant: 'single',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A client’s name',
                            text: 'One paragraph in their words — the sentence you would want a stranger to read before they decide.',
                            bullets: ['The session · the year'],
                        },
                    ],
                },
            },
            {
                type: 'location',
                id: 'find-me',
                slots: {
                    kicker: '05 · The door',
                    headline: 'Find me',
                    address: 'Street and number\nTown, island, postcode',
                    phone: '+1 (340) 555-0100',
                    map: photo('4:3', 'map 4:3'),
                    show_hours: true,
                    directions: true,
                },
            },
            {
                type: 'cta',
                id: 'book',
                slots: {
                    tone: 'accent',
                    kicker: '06 · Book',
                    headline: 'Ready when you are',
                    note: 'Pick a session and a time. You will get a text back.',
                    link_label: 'Book a session',
                    link_target: 'section:sessions',
                },
            },
            {
                type: 'footer',
                id: 'footer',
                slots: { tone: 'night', note: 'Your name — practising in your town since …' },
            },
        ],
        pages: {
            story: {
                title: 'About me',
                sections: [
                    {
                        type: 'story',
                        id: 'story',
                        slots: {
                            kicker: 'About',
                            headline: 'How I got here',
                            body: 'The long version: what you did before this, who taught you, why you work the way you do, and what you are still learning. A person-led business sells a person; this is the page where they get more than a paragraph.',
                            image: photo('3:2', 'at work 3:2'),
                            variant: 'editorial',
                        },
                    },
                ],
            },
            faq: {
                title: 'Questions',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            tone: 'alt',
                            kicker: 'Before you come',
                            headline: 'Questions people ask',
                            variant: 'list',
                            items: [
                                { q: 'What happens in a first session?', a: 'Your answer.' },
                                { q: 'What should I bring or wear?', a: 'Your answer.' },
                                { q: 'Do you take insurance?', a: 'Your answer.' },
                                { q: 'What if I need to reschedule?', a: 'Your policy, plainly.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Sessions', target: 'section:sessions' },
            { label: 'About me', target: 'page:story' },
            { label: 'Questions', target: 'page:faq' },
            { label: 'Find me', target: 'section:find-me' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        {
            where: 'hero',
            ratio: '4:5',
            count: 1,
            note: 'Your portrait, looking at the camera — the frame beside your first sentence.',
        },
        {
            where: 'team',
            ratio: '4:5',
            count: 1,
            note: 'A second portrait, at work rather than posed, for the credentials band.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 1,
            note: 'A small portrait of the one client you quote, or leave it out.',
        },
        { where: 'story', ratio: '3:2', count: 1, note: 'One photograph for the About page.' },
        { where: 'location', ratio: '4:3', count: 1, note: 'A still map or an aerial photograph.' },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **The Studio** — the class shape (§9.5 row 3); built against TCM & Yoga
 * Studio.
 *
 * hero cover → features (the benefits and the intro offer) → services →
 * team avatars → testimonials cards → location → cta.
 *
 * **The timetable is deliberately not here**, and it is the one shape the
 * survey overturned outright: *no* surveyed studio puts its schedule on the
 * home page (1 of 31, and that one is a widget), because every one of them
 * links out to Mindbody, Momence, Mariana Tek or Punchpass. The home leads
 * with the benefits and the new-student price and sends people to the
 * schedule. So `timetable` is the **classes category page's** block
 * (`SECTION_VOCABULARY.timetable.pages` lists `category`), *offered* on the
 * home and not default — the caveat §9.6 records is that their absence is
 * partly tooling, theirs is a page they cannot embed and ours is not, so a
 * studio that wants the week on the home can tick it and stand at eight
 * bands.
 *
 * **`services` is the price table**, which is what the evidence asked for and
 * what phase 8 could not write: barbers and studios put a price table early,
 * at 19 % of the page, as the intro offer, and the vocabulary had no
 * `price-table` view to name, so the block stood as a `list` with a comment
 * saying it would become the table by changing one enum value. Phase 9 landed
 * the view (§9.7) and this is that one enum value changed — name, duration,
 * price, no cards and no descriptions, with the Book verb still on the row.
 * The features band above it keeps the intro *offer* in words; the table
 * underneath is where drop-in, packs and courses line up to be compared.
 *
 * **No booking block**, for the same reason as The Practice: the class page
 * carries the control, and a Book verb on every row is what reaches it.
 */
const THE_STUDIO = {
    name: 'the-studio',
    label: 'The Studio',
    vertical: 'A studio selling classes and treatments',
    client: 'TCM & Yoga Studio',
    blurb: 'The room full, what a first month gives you and what it costs, the classes and treatments as rows, the teachers as a row of faces, what students say, the way there, and the ask.',
    document: {
        preset: 'coastal',
        tagline: 'Classes, treatments, and somewhere to start.',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your street · Your town',
                    headline: 'Your studio, in one line',
                    sub: 'What you teach and who it is for, then the sentence that tells a beginner they are welcome.',
                    cta_label: 'See the classes',
                    cta_target: 'section:classes',
                    variant: 'cover',
                    scrim: 'dusk',
                },
            },
            {
                type: 'features',
                id: 'why-here',
                slots: {
                    kicker: '01 · Why here',
                    headline: 'What you get, and where to start',
                    note: 'Three promises and one offer — the shape every studio in the survey opens with.',
                    variant: 'icons',
                    columns: '4',
                    items: [
                        { icon: 'sun', title: 'The first promise', text: 'One line on why it matters.' },
                        { icon: 'star', title: 'The second', text: 'One line.' },
                        { icon: 'moon', title: 'The third', text: 'One line.' },
                        {
                            icon: 'key',
                            title: 'Your first month',
                            text: 'The intro offer, priced: what a new student pays and what it includes.',
                        },
                    ],
                },
            },
            {
                type: 'services',
                id: 'classes',
                slots: {
                    tone: 'alt',
                    kicker: '02 · Classes and treatments',
                    headline: 'What runs, and what it costs',
                    note: 'Drop-in, packs and courses. The week itself lives on the classes page.',
                    variant: 'price-table',
                    book_verb: true,
                    show_price: true,
                    show_duration: true,
                },
            },
            {
                type: 'team',
                id: 'teachers',
                slots: {
                    kicker: '03 · Who teaches',
                    headline: 'The teachers',
                    variant: 'avatars',
                    items: [
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they teach, in four words.',
                            bullets: [],
                        },
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they teach.',
                            bullets: [],
                        },
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they teach.',
                            bullets: [],
                        },
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they teach.',
                            bullets: [],
                        },
                    ],
                },
            },
            {
                type: 'testimonials',
                id: 'students',
                slots: {
                    kicker: '04 · Students',
                    headline: 'What students say',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A student’s name',
                            text: 'Their words, as they wrote them.',
                            bullets: ['The class · with whom'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'Another name',
                            text: 'Their words.',
                            bullets: ['The class'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A third',
                            text: 'Their words.',
                            bullets: ['The class'],
                        },
                    ],
                },
            },
            {
                type: 'location',
                id: 'find-us',
                slots: {
                    kicker: '05 · The door',
                    headline: 'Find us',
                    address: 'Street and number\nTown, island, postcode',
                    phone: '+1 (340) 555-0100',
                    map: photo('4:3', 'map 4:3'),
                    show_hours: true,
                    directions: true,
                },
            },
            {
                type: 'cta',
                id: 'start',
                slots: {
                    tone: 'accent',
                    kicker: '06 · Start',
                    headline: 'Your first class is the hard one. Book it.',
                    note: 'Arrive ten minutes early and bring nothing.',
                    link_label: 'Book a class',
                    link_target: 'section:classes',
                },
            },
            {
                type: 'footer',
                id: 'footer',
                slots: { tone: 'night', note: 'Your studio — teaching in your town since …' },
            },
        ],
        pages: {
            faq: {
                title: 'Questions',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            tone: 'alt',
                            kicker: 'New here',
                            headline: 'Questions from new students',
                            variant: 'list',
                            items: [
                                { q: 'I have never done this. Which class?', a: 'Your answer.' },
                                { q: 'What do I bring?', a: 'Your answer.' },
                                { q: 'How do packs and memberships work?', a: 'Your answer.' },
                                { q: 'What if I miss a class I booked?', a: 'Your policy, plainly.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Classes', target: 'section:classes' },
            { label: 'Teachers', target: 'section:teachers' },
            { label: 'Questions', target: 'page:faq' },
            { label: 'Find us', target: 'section:find-us' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        {
            where: 'hero',
            ratio: '16:9',
            count: 1,
            note: 'The room with people in it, mid-class, wide.',
        },
        {
            where: 'team',
            ratio: '1:1',
            count: 4,
            note: 'One square portrait per teacher, cropped the same way.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 3,
            note: 'A small portrait per student, or leave them out.',
        },
        { where: 'location', ratio: '4:3', count: 1, note: 'A still map or an aerial photograph.' },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **The Quiet Spa** — the luxury shape (§9.5 row 4); built against Hotel
 * 1829's spa and Alpine Creek's sauna, and the right dress for Love Spa on a
 * quiet day.
 *
 * hero cover → story editorial → services list without prices → gallery
 * filmstrip → features quiet → hours → cta. The references are Aman, Six
 * Senses, the two hotel-spa pages and Clune's three-band minimal: prose over
 * pictures, no price on the row, `hours` as a band rather than a footer line
 * (26 % of real sites, and it is the hotel spas and barbers that carry it),
 * and the photography as one strip that scrolls rather than a grid that
 * shouts.
 *
 * **`show_price: false` is the whole point of the shape** and it is also the
 * survey's majority finding: prices are on the row on 4 of 23 real spa homes.
 * The price is on the treatment's own page, where a person who has decided to
 * read that far will find it.
 *
 * **Three of phase 9's four workarounds were this document's** (§9.7), and all
 * three are now the option itself:
 *  - `hero.align: 'bottom'` — Six Senses' single serif sentence at the *foot*
 *    of the picture. The hero stood at `cover` + `left` and read correctly;
 *    one line changed when the option landed, exactly as this comment
 *    predicted.
 *  - `cta.variant: 'minimal'` — one line and one underlined text link on the
 *    page's own ground, Aman's close. It used to be made out of the band
 *    vocabulary (a `paper` ground and no accent), which could say where the
 *    band sat but never that the button was not a button.
 *  - the `features` items **carry no icon names**, because the `quiet` view
 *    draws none. They used to carry three that were never drawn, purely so the
 *    strict write path would accept the document.
 *
 * **The preset is `serene`**, which is the quietest of the four by
 * construction: the widest air (`space.section`), the softest edge, a cool
 * serif over a humanist sans, a near-neutral stone ground. `gilded` is
 * warmer, `coastal` is louder, `bistro` is tighter.
 */
const THE_QUIET_SPA = {
    name: 'the-quiet-spa',
    label: 'The Quiet Spa',
    vertical: 'A hotel spa, a sauna, or a spa that would rather whisper',
    client: 'Hotel 1829 · Alpine Creek',
    blurb: 'A photograph and one sentence, prose rather than pitch, the treatments named but not priced, the place as a strip you scroll, what is always true, the week, and a quiet ask.',
    document: {
        preset: 'serene',
        tagline: 'Quiet hours, kept.',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your house · Your island',
                    headline: 'One sentence, and nothing else',
                    sub: 'A second sentence only if the first needs it.',
                    cta_label: 'Reserve',
                    cta_target: 'section:treatments',
                    variant: 'cover',
                    align: 'bottom',
                    scrim: 'dusk',
                },
            },
            {
                type: 'story',
                id: 'the-house',
                slots: {
                    kicker: '01',
                    headline: 'What this place is',
                    body: 'Three short paragraphs, unhurried: where the building came from, what the treatments are built around, and what an afternoon here is actually like. No exclamation marks and no offers.',
                    variant: 'editorial',
                },
            },
            {
                type: 'services',
                id: 'treatments',
                slots: {
                    kicker: '02',
                    headline: 'Treatments',
                    note: 'Each treatment’s own page carries the length, the price and the times.',
                    variant: 'list',
                    show_price: false,
                    show_duration: true,
                    book_verb: false,
                },
            },
            {
                type: 'gallery',
                id: 'the-rooms',
                slots: {
                    kicker: '03',
                    headline: 'The rooms',
                    variant: 'filmstrip',
                    images: [
                        { src: photo('3:2', 'room 3:2'), alt: 'A treatment room', caption: '' },
                        { src: photo('3:2', 'room 3:2'), alt: 'The water', caption: '' },
                        { src: photo('3:2', 'room 3:2'), alt: 'The terrace', caption: '' },
                        { src: photo('3:2', 'room 3:2'), alt: 'A detail', caption: '' },
                        { src: photo('3:2', 'room 3:2'), alt: 'The corridor', caption: '' },
                        { src: photo('3:2', 'room 3:2'), alt: 'The view', caption: '' },
                    ],
                },
            },
            {
                type: 'features',
                id: 'always',
                slots: {
                    tone: 'alt',
                    kicker: '04',
                    headline: 'Always',
                    variant: 'quiet',
                    columns: '3',
                    // No icons, because the `quiet` view draws none — the contract says
                    // so on the `items` slot now, so the names that were here and unused
                    // are simply gone.
                    items: [
                        { title: 'The first', text: 'One line, in the register of the story above it.' },
                        { title: 'The second', text: 'One line.' },
                        { title: 'The third', text: 'One line.' },
                    ],
                },
            },
            {
                type: 'hours',
                id: 'hours',
                slots: {
                    kicker: '05',
                    headline: 'Hours',
                    note: 'Leave the rows empty and the block prints the week the book actually keeps.',
                },
            },
            {
                type: 'cta',
                id: 'reserve',
                slots: {
                    kicker: '06',
                    headline: 'Reserve',
                    note: 'Or ask at the desk.',
                    variant: 'minimal',
                    link_label: 'Reserve',
                    link_target: 'section:treatments',
                },
            },
            {
                type: 'footer',
                id: 'footer',
                slots: { tone: 'night', note: 'Your house — your street, your island.' },
            },
        ],
        pages: {
            faq: {
                title: 'Before you come',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            kicker: 'Before you come',
                            headline: 'Before you come',
                            variant: 'list',
                            items: [
                                { q: 'How early should I arrive?', a: 'Your answer.' },
                                { q: 'Is the spa open to people who are not staying?', a: 'Your answer.' },
                                { q: 'What is provided, and what should I bring?', a: 'Your answer.' },
                                { q: 'What is the cancellation window?', a: 'Your policy, plainly.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Treatments', target: 'section:treatments' },
            { label: 'The rooms', target: 'section:the-rooms' },
            { label: 'Hours', target: 'section:hours' },
            { label: 'Before you come', target: 'page:faq' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        {
            where: 'hero',
            ratio: '16:9',
            count: 1,
            note: 'One photograph the whole page rests on — water, stone or light, no people.',
        },
        {
            where: 'gallery',
            ratio: '3:2',
            count: 6,
            note: 'The rooms as a strip that scrolls: room, water, terrace, a detail, the corridor, the view.',
        },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **The Venue** — the book-first shape (§9.5 row 5): the one home page with
 * the control on it.
 *
 * hero editorial → gallery mosaic → services rows with Book → **booking rail**
 * → team avatars → testimonials cards → location → cta.
 *
 * **Nobody's default, and that is the finding.** This is a *marketplace*
 * shape — it is what a Fresha or Booksy venue page looks like — and exactly
 * one small business in the survey of thirty-one builds its own home this
 * way. The booking control is on 2 of 31 real home pages. So the template
 * exists because the shape is real and a tenant may want it, and it is
 * offered to nobody by default; `docs/clients.md` has no client beside it.
 *
 * **Where the mosaic went.** §9.5 writes this shape as "hero editorial with
 * the photo mosaic", which the references draw as one block. Our `hero` has
 * no gallery inside it — `editorial` "stacks the words over the photograph as
 * a wide strip beneath them", one image, not four — so the mosaic is its own
 * band directly under the hero, which is what the venue pages render anyway
 * once you look at the markup rather than the screenshot. `gallery.mosaic` is
 * the view that exists for exactly this ("one large frame with the rest tiled
 * beside it, the venue-page shape").
 *
 * **Seven bands, and the control is not one of them** — the same count One
 * Room uses and the same reason: §9.4 excludes the booking control from the
 * seven, and `PINNED_SECTION` means the flow mounts regardless. What makes
 * this shape different is not that it *has* the block but that it *places*
 * it, high, right under the offer, instead of leaving it at the foot.
 */
const THE_VENUE = {
    name: 'the-venue',
    label: 'The Venue',
    vertical: 'Book-first: the control on the home page',
    client: 'Nobody’s default — the marketplace shape, offered',
    blurb: 'A headline over one photograph, the place as a mosaic, every service as a row with a Book, the picker itself with its summary beside it, the people, the reviews, the way there.',
    document: {
        preset: 'coastal',
        tagline: 'Everything on one page, and the book on it.',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your street · Your town',
                    headline: 'Your name, and what you do',
                    sub: 'One sentence. The button goes straight to the picker below.',
                    cta_label: 'Book now',
                    cta_target: 'section:booking',
                    variant: 'editorial',
                    align: 'left',
                },
            },
            {
                type: 'gallery',
                id: 'the-place',
                slots: {
                    headline: 'The place',
                    variant: 'mosaic',
                    images: [
                        { src: photo('3:2', 'place 3:2'), alt: 'The room', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'The chairs', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'The front', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'A detail', caption: '' },
                        { src: photo('3:2', 'place 3:2'), alt: 'The street', caption: '' },
                    ],
                },
            },
            {
                type: 'services',
                id: 'services',
                slots: {
                    kicker: '01 · Services',
                    headline: 'Everything we do, with a Book on every row',
                    note: 'Prices and durations come from the book itself.',
                    variant: 'list',
                    book_verb: true,
                    show_price: true,
                    show_duration: true,
                },
            },
            /*
             * The control, placed rather than pinned to the foot — the one thing
             * that makes this a venue page. Still outside the seven (§9.4).
             *
             * **`rail`, and it stays `rail` while One Room moves to `card`.** This
             * shape's whole point is the summary standing beside the service rows
             * through every step — Fresha's and Booksy's venue page, where a guest
             * is choosing among many services and the accumulating card is what
             * tells them where they are. The card is one panel about one service and
             * has no summary column to keep; on a venue page it would answer a
             * question this template is not asking.
             */
            {
                type: 'booking',
                id: 'booking',
                slots: {
                    tone: 'alt',
                    kicker: '02 · Book',
                    headline: 'Book',
                    note: 'Pick a service, a person and a time. The summary stays beside you.',
                    variant: 'rail',
                },
            },
            {
                type: 'team',
                id: 'team',
                slots: {
                    kicker: '03 · The team',
                    headline: 'Who is in today',
                    variant: 'avatars',
                    items: [
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they do, in four words.',
                            bullets: [],
                        },
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they do.',
                            bullets: [],
                        },
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they do.',
                            bullets: [],
                        },
                        {
                            image: photo('1:1', 'face 1:1'),
                            title: 'A name',
                            text: 'What they do.',
                            bullets: [],
                        },
                    ],
                },
            },
            {
                type: 'testimonials',
                id: 'reviews',
                slots: {
                    kicker: '04 · Reviews',
                    headline: 'What people say',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A name',
                            text: 'Their words, as they wrote them.',
                            bullets: ['The service · with whom'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'Another name',
                            text: 'Their words.',
                            bullets: ['The service'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A third',
                            text: 'Their words.',
                            bullets: ['The service'],
                        },
                    ],
                },
            },
            {
                type: 'location',
                id: 'find-us',
                slots: {
                    kicker: '05 · The door',
                    headline: 'Find us',
                    address: 'Street and number\nTown, island, postcode',
                    phone: '+1 (340) 555-0100',
                    map: photo('4:3', 'map 4:3'),
                    show_hours: true,
                    directions: true,
                },
            },
            {
                type: 'cta',
                id: 'book-again',
                slots: {
                    tone: 'accent',
                    kicker: '06 · Book',
                    headline: 'Pick a time',
                    note: 'Same picker, in case you scrolled past it.',
                    link_label: 'Book now',
                    link_target: 'section:booking',
                },
            },
            {
                type: 'footer',
                id: 'footer',
                slots: { tone: 'night', note: 'Your name — your street, your town.' },
            },
        ],
        pages: {
            faq: {
                title: 'Questions',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            tone: 'alt',
                            kicker: 'Good questions',
                            headline: 'Good questions',
                            variant: 'pills',
                            items: [
                                { q: 'Do you take walk-ins?', a: 'Your answer.' },
                                { q: 'Where do I park?', a: 'Your answer.' },
                                { q: 'Can I ask for a particular person?', a: 'Your answer.' },
                                { q: 'What if I need to cancel?', a: 'Your policy, plainly.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Book', target: 'section:booking' },
            { label: 'Services', target: 'section:services' },
            { label: 'The team', target: 'section:team' },
            { label: 'Questions', target: 'page:faq' },
            { label: 'Find us', target: 'section:find-us' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        {
            where: 'hero',
            ratio: '16:9',
            count: 1,
            note: 'One wide photograph under the headline — the front, or the room from the door.',
        },
        {
            where: 'gallery',
            ratio: '3:2',
            count: 5,
            note: 'The mosaic: one that carries the page, four that fill in beside it.',
        },
        {
            where: 'team',
            ratio: '1:1',
            count: 4,
            note: 'One square portrait per person, cropped the same way.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 3,
            note: 'A small portrait per reviewer, or leave them out.',
        },
        { where: 'location', ratio: '4:3', count: 1, note: 'A still map or an aerial photograph.' },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **The Table** — a restaurant, re-cut from the restaurant survey
 * (`docs/design/references/homepage-sequences-verticals.md` §1 and §8); built
 * against China King.
 *
 * **What was overturned: the search hero, and the ask itself.** §4.3 opened
 * this template with the party · date · time bar and made *reservations* the
 * thing the page is for. The survey found **no search hero on any of nine**
 * restaurants (§1) — the single party · date · time control it did find sits
 * in Tavern on the Green's *footer* — and reservations are a button or a
 * link-out everywhere they exist at all. The owner's steer, recorded before
 * the tally, was that the thing to be after is **online ordering**; §1 agrees,
 * because the two sites that sell on the page sell by ordering.
 *
 * **Which rows decided each band** (§1 unless said otherwise):
 *  - `hero cover`, the button saying **Order now** — China King's own template
 *    opens on order-type and time pickers, Standard Baking's hero sits over a
 *    cart; Cuvée's "Reserve" is the shape this template deliberately is not.
 *  - `menu` at band 2 with **ordering on** — China King (a 24-category grid at
 *    band 3, the page *is* the menu) and Standard Baking (twelve products with
 *    prices and sold-out marks at band 4). First sell is band 3 on the median
 *    restaurant (§7), and a takeout kitchen's is earlier.
 *  - `story split` — 8 of 9 carry a story; Pequod's, Mama's Fish House,
 *    Commander's Palace and Gladys' all run it after the offer, not before.
 *  - `gallery mosaic` — the plates, which is what a restaurant's photography
 *    is; Gladys' and Mama's Fish House both carry one.
 *  - `testimonials single` — Mama's Fish House (Beard, TripAdvisor),
 *    Pequod's (Yelp #1), Commander's Palace (six press marks). One quote is
 *    the register a single room can carry honestly.
 *  - `location` with the week folded in — **the closing band on restaurants
 *    and lodging** (§7: `location` at 100 % position, 36 % of all 44), and
 *    six of nine put phone or address in the masthead besides. Becky's Diner
 *    is hours + location and almost nothing else.
 *  - `cta` last — Commander's Palace and Standard Baking both end on one, and
 *    a page whose ask is ordering should ask twice.
 *
 * **Reservations are the add-on, not the default.** A tenant who takes tables
 * adds a `booking` band with `variant: 'inline'` — the study's party · date ·
 * time control, in the page rather than as the hero — and stands at seven
 * bands still, because the booking block is outside the count (§9.4,
 * `PINNED_SECTION`): the flow mounts whether or not the block is arranged, so
 * a restaurant that never arranges one still takes reservations through the
 * shell.
 *
 * **Hours and the address in the masthead** is §8's second open gap and a
 * later pass: the survey's finding is that the restaurant masthead does the
 * finding, and the masthead is a branding key with tokens rather than a block
 * with slots, so it lands as a masthead token and not as a band here.
 *
 * **No `pages` by default.** The restaurant home the survey found answers with
 * the menu and the door; there was never an FAQ band to move.
 */
const THE_TABLE = {
    name: 'the-table',
    label: 'The Table',
    vertical: 'A restaurant',
    client: 'China King',
    blurb: 'The room in a photograph with Order now on it, the menu from the kitchen’s own catalog with a basket, where the food comes from, the dishes as a mosaic, what people say, the door with the week in a line, and the ask again.',
    document: {
        preset: 'bistro',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your neighbourhood',
                    headline: 'Your kitchen, in one line',
                    sub: 'What you cook and who it is for — one sentence, then how long an order takes.',
                    cta_label: 'Order now',
                    cta_target: 'section:menu',
                    variant: 'cover',
                    scrim: 'dusk',
                },
            },
            {
                type: 'menu',
                id: 'menu',
                slots: {
                    kicker: '01 · The menu',
                    headline: 'Order from the kitchen',
                    // Pick-up and nothing else: this platform takes no delivery order, so
                    // the placeholder a tenant starts from does not promise one (owner's
                    // ruling 5; `FactSeg` in `apps/site/src/ui/TripBar.tsx`).
                    note: 'Every dish, by category, with a basket on the page. Orders are for pick-up, and the time is chosen in the basket.',
                    show_prices: true,
                    allow_ordering: true,
                },
            },
            {
                type: 'story',
                id: 'the-story',
                slots: {
                    kicker: '02 · The house',
                    headline: 'Where this food comes from',
                    body: 'Two or three sentences: the family, the region, the one dish everybody orders. The paragraph a stranger reads while the order is being made.',
                    image: photo('3:2', 'room 3:2'),
                    variant: 'split',
                },
            },
            {
                type: 'gallery',
                id: 'dishes',
                slots: {
                    tone: 'alt',
                    kicker: '03 · The plates',
                    headline: 'Six dishes',
                    variant: 'mosaic',
                    images: [
                        { src: photo('1:1', 'dish 1:1'), alt: 'A dish', caption: '' },
                        { src: photo('1:1', 'dish 1:1'), alt: 'A dish', caption: '' },
                        { src: photo('1:1', 'dish 1:1'), alt: 'A dish', caption: '' },
                        { src: photo('1:1', 'dish 1:1'), alt: 'A dish', caption: '' },
                        { src: photo('1:1', 'dish 1:1'), alt: 'A dish', caption: '' },
                        { src: photo('1:1', 'dish 1:1'), alt: 'A dish', caption: '' },
                    ],
                },
            },
            {
                type: 'testimonials',
                id: 'guests',
                slots: {
                    kicker: '04 · Guests',
                    headline: 'What people say',
                    variant: 'single',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A guest’s name',
                            text: 'One sentence that says it all — the review you would put in the window.',
                            bullets: [],
                        },
                    ],
                },
            },
            {
                type: 'location',
                id: 'find-us',
                slots: {
                    tone: 'alt',
                    kicker: '05 · The door',
                    headline: 'Find us',
                    address: 'Street and number\nTown, island, postcode',
                    phone: '+1 (340) 555-0100',
                    map: photo('4:3', 'map 4:3'),
                    show_hours: true,
                    directions: true,
                    note: 'The kitchen closes half an hour before the dining room — say so here.',
                },
            },
            {
                type: 'cta',
                id: 'order',
                slots: {
                    tone: 'accent',
                    kicker: '06 · Order',
                    headline: 'Hungry now?',
                    note: 'Order online and we will text you when it is ready.',
                    link_label: 'Order now',
                    link_target: 'section:menu',
                },
            },
            { type: 'footer', id: 'footer', slots: { tone: 'night', note: 'Your name — since …' } },
        ],
        nav: [
            { label: 'Menu', target: 'section:menu' },
            { label: 'The house', target: 'section:the-story' },
            { label: 'Find us', target: 'section:find-us' },
            { label: 'Basket', target: 'cart' },
            { label: 'My orders', target: 'visits' },
        ],
    },
    shopping: [
        {
            where: 'hero',
            ratio: '16:9',
            count: 1,
            note: 'The room at service, or the street at night.',
        },
        {
            where: 'story',
            ratio: '3:2',
            count: 1,
            note: 'The kitchen, the family, or the room by day.',
        },
        {
            where: 'gallery',
            ratio: '1:1',
            count: 6,
            note: 'Six dishes, shot from above on the same table.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 1,
            note: 'A small portrait of the one guest you quote, or leave it out.',
        },
        {
            where: 'menu',
            ratio: '1:1',
            count: 0,
            note: 'Dish photographs go on the items themselves, in the catalog — one per dish you want pictured.',
        },
        { where: 'location', ratio: '4:3', count: 1, note: 'A still map or the front of the house.' },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **Stays** — a lodge or small hotel, re-cut from the lodging survey
 * (`docs/design/references/homepage-sequences-verticals.md` §2 and §8); built
 * against Hotel 1829.
 *
 * **What was overturned: the search hero.** §4.3 made the dates · guests bar
 * the hero. **Not one of ten** small lodging sites does that (§2) — the
 * closest, Playa Nicuesa (§5), runs a dates · guests *strip* as a band above
 * the hero, not as it. What ten of ten do have is a Book button in the
 * masthead (8 of 10) and a **booking band at band 3 or 4**: a Book Direct
 * promise on Hotel Caravelle and L'Habitation, the real widget on Beaufort
 * House. So the control stays on the page and stops being the first thing a
 * guest sees; the picture is.
 *
 * **Which rows decided each band** (§2 unless said otherwise):
 *  - `hero cover` — 10 of 10.
 *  - `stay` at band 2 — the booking band, one band earlier than the median
 *    because ours is the real control rather than a promise with a button:
 *    Hotel Caravelle band 3, L'Habitation band 3, Victoria House band 4,
 *    The Vermont Inn band 4, Beaufort House band 5.
 *  - `story split` at band 3 — **on every one of the ten**, at band 2 or 3.
 *  - `showcase cards` for the rooms — rooms as cards on half the sites
 *    (L'Habitation's seven, Mahayana's five, Eiger View's eight), each card
 *    going to that room type's own page.
 *  - `features pillars` — the amenities band; Victoria House, The Vermont Inn,
 *    Hideaway Mountain, Holly Hill.
 *  - `gallery filmstrip` — **8 of 10**, and it repeats: a carousel early and a
 *    themed set late. One strip is the honest single version of that.
 *  - `location` closing — 4 of 10 close on it, more than close on anything
 *    else, and §7 puts `location` at 100 % position across all forty-four.
 *
 * **The cta goes.** It was §4.3's closer here and the survey does not support
 * it: 2 of 10, and the band that closes lodging is the address. The hero's
 * button and the `stay` band are the two asks this page needs.
 *
 * `show_hours` is off on the location band: a hotel's front desk is not a
 * week of opening times, and printing one would say something false about a
 * business that is open at three in the morning.
 *
 * **`pages.faq` stays** where phase 7 put it — check-in, breakfast, the
 * cancellation window are the questions lodging actually answers, and no
 * surveyed site carries an FAQ band on the home.
 */
const STAYS = {
    name: 'stays',
    label: 'Stays',
    vertical: 'A lodge or small hotel',
    client: 'Hotel 1829',
    blurb: 'The house in a photograph, two dates and a party size, how the house came to be, each room as a card that goes to its own page, three promises as pillars, the grounds as a strip you scroll, and the way there.',
    document: {
        preset: 'gilded',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your town · Since …',
                    headline: 'Your house, in one line',
                    sub: 'What a night here is like, in a sentence — then the dates.',
                    cta_label: 'Check dates',
                    cta_target: 'section:rooms',
                    variant: 'cover',
                    scrim: 'dusk',
                },
            },
            {
                type: 'stay',
                id: 'rooms',
                slots: {
                    kicker: '01 · Dates',
                    headline: 'Rooms',
                    note: 'Two dates and a party size. What comes back is what is free and what it costs for those nights — never a rate that is right for some and wrong for others.',
                    default_nights: 2,
                },
            },
            {
                type: 'story',
                id: 'the-story',
                slots: {
                    kicker: '02 · The house',
                    headline: 'How this house came to be',
                    body: 'Three or four sentences: who built it, who keeps it, what a morning here sounds like. The long version belongs on a page of its own; this is the one a guest reads while the dates load.',
                    image: photo('3:2', 'house 3:2'),
                    variant: 'split',
                },
            },
            {
                type: 'showcase',
                id: 'the-rooms',
                slots: {
                    tone: 'alt',
                    kicker: '03 · Each room',
                    headline: 'The rooms',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('4:3', 'room 4:3'),
                            title: 'A room type',
                            text: 'One line on what makes it this room.',
                            bullets: ['Sleeps …', 'The view'],
                            link: 'category:harbour-rooms',
                            link_label: 'See the room',
                        },
                        {
                            image: photo('4:3', 'room 4:3'),
                            title: 'Another',
                            text: 'One line.',
                            bullets: ['Sleeps …'],
                            link: 'category:garden-rooms',
                            link_label: 'See the room',
                        },
                        {
                            image: photo('4:3', 'room 4:3'),
                            title: 'A third',
                            text: 'One line.',
                            bullets: ['Sleeps …'],
                            link: 'category:suites',
                            link_label: 'See the room',
                        },
                    ],
                },
            },
            {
                type: 'features',
                id: 'promises',
                slots: {
                    kicker: '04 · Always',
                    headline: 'Three promises',
                    variant: 'pillars',
                    columns: '3',
                    items: [
                        {
                            icon: 'sun',
                            title: 'The first',
                            text: 'One line.',
                            image: photo('3:4', 'pillar 3:4'),
                        },
                        {
                            icon: 'mug',
                            title: 'The second',
                            text: 'One line.',
                            image: photo('3:4', 'pillar 3:4'),
                        },
                        {
                            icon: 'bed',
                            title: 'The third',
                            text: 'One line.',
                            image: photo('3:4', 'pillar 3:4'),
                        },
                    ],
                },
            },
            {
                type: 'gallery',
                id: 'grounds',
                slots: {
                    kicker: '05 · The grounds',
                    headline: 'Around the house',
                    variant: 'filmstrip',
                    images: [
                        { src: photo('3:2', 'grounds 3:2'), alt: 'The grounds', caption: '' },
                        { src: photo('3:2', 'grounds 3:2'), alt: 'The terrace', caption: '' },
                        { src: photo('3:2', 'grounds 3:2'), alt: 'The pool', caption: '' },
                        { src: photo('3:2', 'grounds 3:2'), alt: 'Breakfast', caption: '' },
                    ],
                },
            },
            {
                type: 'location',
                id: 'find-us',
                slots: {
                    kicker: '06 · The door',
                    headline: 'Find us',
                    address: 'Street and number\nTown, island, postcode',
                    phone: '+1 (340) 555-0100',
                    map: photo('4:3', 'map 4:3'),
                    show_hours: false,
                    directions: true,
                    note: 'Check-in from …, check-out by …. Say where to park.',
                },
            },
            { type: 'footer', id: 'footer', slots: { tone: 'night', note: 'Your name — since …' } },
        ],
        pages: {
            faq: {
                title: 'Questions',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            tone: 'alt',
                            kicker: 'Good questions',
                            headline: 'Good questions',
                            variant: 'list',
                            items: [
                                { q: 'When can I check in?', a: 'Your answer.' },
                                { q: 'Is breakfast included?', a: 'Your answer.' },
                                { q: 'What if I need to cancel?', a: 'Your policy, plainly.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Dates', target: 'section:rooms' },
            { label: 'The rooms', target: 'section:the-rooms' },
            { label: 'The house', target: 'section:the-story' },
            { label: 'FAQ', target: 'page:faq' },
            { label: 'Find us', target: 'section:find-us' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        { where: 'hero', ratio: '16:9', count: 1, note: 'The house at dusk, wide.' },
        { where: 'story', ratio: '3:2', count: 1, note: 'The house by day, or whoever keeps it.' },
        {
            where: 'showcase',
            ratio: '4:3',
            count: 3,
            note: 'One photograph per room type, bed made, window in — the cards go to /c/harbour-rooms, /c/garden-rooms and /c/suites, so name your categories to match or retarget the cards.',
        },
        {
            where: 'features',
            ratio: '3:4',
            count: 3,
            note: 'Three tall photographs: the pool, the terrace, the breakfast.',
        },
        { where: 'gallery', ratio: '3:2', count: 4, note: 'The grounds, four ways, as one strip.' },
        { where: 'location', ratio: '4:3', count: 1, note: 'A still map or an aerial photograph.' },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **Departures** — a tour or charter operator, cut from the operator surveys
 * (`docs/design/references/homepage-sequences-verticals.md` §3 and §8, then
 * amended by `docs/design/references/homepage-sequences-showcase.md` §4);
 * built against Pink Perry VI.
 *
 * **What was overturned, twice: the board's place.** §4.3 put the sailings
 * board at band 2 on taste. §8's search-picked cohort of eight moved it to
 * band 3, behind the captain, because story sat at band 2 on every one of
 * them. The **showcase survey** then overturned that: ten sites taken only
 * from the tour vendors' own customer showcases (FareHarbor Sites, Xola,
 * Peek, TourismTiger) — the cohort a search cannot reach, because a
 * vendor-built site ranks on its operator's name and not on "best tour
 * company" — run the trip cards **before** the story on five of the six that
 * carry both (Jolly Pirates, Carabalí, Jamie's, Danger Charters, Cruise San
 * Diego are all hero → tours → … → story), put the first selling band at
 * band 2–3 on seven of ten, and leave the story at a median 56 % of the page.
 * So the board is back at **band 2** and the captain follows it at band 3.
 * Two surveys disagreed and the vendor-built cohort wins this vertical: it is
 * what the people who sell tour websites for a living ship, and it is the
 * only cohort selected without a ranking that rewards content over selling.
 *
 * **Which rows decided each band** (§4 of the showcase note unless said
 * otherwise):
 *  - `hero cover` — 8 of 8 in §3, and every showcase site besides; four of
 *    the first eight are video.
 *  - `sailings` at band 2 — the trip cards are band 2 on Jolly Pirates,
 *    Carabalí, Jamie's and Cruise San Diego, band 3 on Danger Charters, and
 *    the first sell is at band 2–3 on seven of the ten. **No home page in
 *    either cohort shows a real date** (§7: 0 of 44) — the cards send you to
 *    a FareHarbor or Xola overlay to discover them — which is exactly why
 *    ours carries the board the Provider API makes possible.
 *  - `story split`, short, the captain, at band 3 — the story's median
 *    position is 56 % of the page and it is *after* the trips on five of six;
 *    Sunshine Daydream and Jammin Jeep are a paragraph and a face, which is
 *    the length this band is still written for.
 *  - `features` 4-up, numbered — the "why us" band; Jammin Jeep, Horizon
 *    Divers (twice), Sunshine Daydream (twice), Charleston Culinary.
 *  - `testimonials cards` — 5 of 8, two thirds down the page; Ocean Surfari
 *    leads with its 4.9, Sunset Sail puts a reviews badge at band 2.
 *  - `gallery grid` — Horizon Divers and Monterey Bay; the proof that the
 *    trip is what the trip says it is.
 *  - `location` closing — the dock is the thing a guest has to find at seven
 *    in the morning; Jammin Jeep, Horizon Divers and Monterey Bay all close
 *    on it or on contact.
 *
 * **Pink Perry's pages stay.** What to bring and what happens if the weather
 * turns are the two questions every operator answers, and the survey found no
 * FAQ band on any home.
 */
const DEPARTURES = {
    name: 'departures',
    label: 'Departures',
    vertical: 'A tour operator — boats, jeeps, guides',
    client: 'Pink Perry VI',
    blurb: 'The boat under way, the board of what is actually leaving and what is left on it, the captain in a sentence beside their portrait, four promises numbered, what guests said, the trips as a grid, and the dock.',
    document: {
        preset: 'coastal',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your harbour',
                    headline: 'Your trips, in one line',
                    sub: 'Where you go and who comes along — one sentence, then the board.',
                    cta_label: 'See what’s sailing',
                    cta_target: 'section:sailings',
                    variant: 'cover',
                    scrim: 'dusk',
                },
            },
            {
                type: 'sailings',
                id: 'sailings',
                slots: {
                    tone: 'alt',
                    kicker: '01 · The board',
                    headline: 'What’s sailing',
                    note: 'Real dates and the places left on each one — the thing every operator in both surveys hides behind a booking overlay.',
                    days_shown: 7,
                },
            },
            {
                type: 'story',
                id: 'the-captain',
                slots: {
                    kicker: '02 · The captain',
                    headline: 'Your name',
                    body: 'Two or three sentences in your own voice: how long you have run this water, what you do differently, why you still like the six o’clock start. Short, and under the board — a reader who scrolled past the trips is asking who you are, not being introduced.',
                    image: photo('4:5', 'portrait 4:5'),
                    variant: 'split',
                },
            },
            {
                type: 'features',
                id: 'promises',
                slots: {
                    kicker: '03 · Every trip',
                    headline: 'Four promises',
                    variant: 'numbered',
                    columns: '4',
                    items: [
                        { icon: 'sun', title: 'The first', text: 'One line.' },
                        { icon: 'map', title: 'The second', text: 'One line.' },
                        { icon: 'star', title: 'The third', text: 'One line.' },
                        { icon: 'key', title: 'The fourth', text: 'One line.' },
                    ],
                },
            },
            {
                type: 'testimonials',
                id: 'guests',
                slots: {
                    kicker: '04 · Guests',
                    headline: 'What guests say',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A guest’s name',
                            text: 'Their words, as they wrote them.',
                            bullets: ['The trip · the month'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'Another name',
                            text: 'Their words.',
                            bullets: ['The trip'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A third',
                            text: 'Their words.',
                            bullets: ['The trip'],
                        },
                    ],
                },
            },
            {
                type: 'gallery',
                id: 'trips',
                slots: {
                    kicker: '05 · The trips',
                    headline: 'Out there',
                    variant: 'grid',
                    columns: '4',
                    images: [
                        { src: photo('4:3', 'trip 4:3'), alt: 'A trip', caption: '' },
                        { src: photo('4:3', 'trip 4:3'), alt: 'A trip', caption: '' },
                        { src: photo('4:3', 'trip 4:3'), alt: 'A trip', caption: '' },
                        { src: photo('4:3', 'trip 4:3'), alt: 'A trip', caption: '' },
                    ],
                },
            },
            {
                type: 'location',
                id: 'the-dock',
                slots: {
                    kicker: '06 · The dock',
                    headline: 'Where we leave from',
                    address: 'The dock, the slip number\nTown, island, postcode',
                    phone: '+1 (340) 555-0100',
                    map: photo('4:3', 'map 4:3'),
                    show_hours: true,
                    directions: true,
                    note: 'Where to park, how early to be there, and what happens if you are late.',
                },
            },
            { type: 'footer', id: 'footer', slots: { tone: 'night', note: 'Your name — since …' } },
        ],
        pages: {
            faq: {
                title: 'Questions',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            tone: 'alt',
                            kicker: 'Good questions',
                            headline: 'Good questions',
                            variant: 'list',
                            items: [
                                { q: 'What should I bring?', a: 'Your answer.' },
                                { q: 'What if the weather turns?', a: 'Your policy, plainly.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Trips', target: 'section:sailings' },
            { label: 'The captain', target: 'section:the-captain' },
            { label: 'Questions', target: 'page:faq' },
            { label: 'The dock', target: 'section:the-dock' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        { where: 'hero', ratio: '16:9', count: 1, note: 'The boat or the jeep, under way.' },
        {
            where: 'sailings',
            ratio: '3:2',
            count: 0,
            note: 'Trip photographs go on the items themselves, in the catalog.',
        },
        {
            where: 'story',
            ratio: '4:5',
            count: 1,
            note: 'The captain’s portrait, on the boat rather than posed, beside the paragraph.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 3,
            note: 'A small portrait per guest, or leave them out.',
        },
        { where: 'gallery', ratio: '4:3', count: 4, note: 'One per trip, guests in the frame.' },
        { where: 'location', ratio: '4:3', count: 1, note: 'A still map, or the dock from the road.' },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **The Shelf** — a shop with fulfillment, re-cut from the brand-shop survey
 * (`docs/design/references/homepage-sequences-verticals.md` §4 and §8); built
 * against the merch verticals (Pink Perry's prints, Love Spa's shelf).
 *
 * **What was overturned: the authored shelf.** §4.3 sold the products as a
 * `showcase cards` band — a picture, a title and a line somebody typed —
 * because the real `shop` block did not exist when the template was written.
 * It does now, and the survey settles it: **products on the home on 7 of 8**
 * shops (§4), with prices and sold-out marks on the card, which is the catalog
 * and nothing a branding row can hold (B7). So band 2 is the catalog grid, and
 * the authored cards move to band 5 where they are honest — *collections*,
 * the tiles 5 of 8 shops carry.
 *
 * **The console preview shows nothing at band 2 until the tenant stocks
 * items**, and that is the honest shape rather than a hole: `shop` renders
 * nothing unless `/api/site` says there is something to sell, which is the
 * same promise that lets a spa and a print shop share one arrangement.
 * Standing a picture there instead would be a page that lies while it is
 * empty and lies differently once it is full.
 *
 * **Which rows decided each band** (§4 unless said otherwise):
 *  - `hero editorial` — 7 of 8 open on one, words over a wide strip.
 *  - `shop` at band 2 — Bungalow Candle Studio (band 3), JHP Store (band 2,
 *    and the whole page), Roam2Rescue (band 4). The median first sell across
 *    the shops is band 4; a maker with one shelf gets there sooner.
 *  - `story split-reverse` at band 3 — 7 of 8, early for apparel (PHADE,
 *    Tee's Caribbean: identity first) and late for makers; this is the
 *    apparel placement, because a small shelf sells the hands behind it.
 *  - `features icons` — the shipping and trust promises: Viva's shipping and
 *    pickup, Hyssop's, Roam2Rescue's trust badges.
 *  - `showcase cards` as collections — St. John Spice's six tiles, PHADE's
 *    six, Tee's two rows; each goes to a category page.
 *  - `testimonials cards` — Hyssop (twice), Bungalow, and the rating strips
 *    every Shopify theme ships.
 *  - `newsletter card` **closing** — 5 of 8, always in the last three bands.
 *    The shop is the one vertical where the newsletter is the closing band
 *    (§7), and the cta is not: it is the interest captured when the guest is
 *    not ready to buy today.
 *
 * **No `pages` by default** — the shop answers shipping and returns in the
 * features band, and no surveyed shop carries an FAQ on the home (2 of 8 have
 * one at all, both near the foot).
 */
const THE_SHELF = {
    name: 'the-shelf',
    label: 'The Shelf',
    vertical: 'A shop with fulfillment',
    client: 'Pink Perry VI (prints), Love Spa (the shelf)',
    blurb: 'The words first and the photograph under them, everything in stock with its price, the maker beside a picture, what shipping and returns promise, the collections as cards, what people say, and one field to stay in touch.',
    document: {
        preset: 'coastal',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Made in …',
                    headline: 'What you make, in one line',
                    sub: 'One sentence on the shelf and where it ships.',
                    cta_label: 'See the shelf',
                    cta_target: 'section:shelf',
                    variant: 'editorial',
                    align: 'center',
                },
            },
            {
                type: 'shop',
                id: 'shelf',
                slots: {
                    kicker: '01 · The shelf',
                    headline: 'On the shelf',
                    note: 'Everything in stock, priced from the catalog. Nothing shows here until you have stocked something — the grid is the catalog, not a picture of one.',
                    show_prices: true,
                },
            },
            {
                type: 'story',
                id: 'the-maker',
                slots: {
                    tone: 'alt',
                    kicker: '02 · The maker',
                    headline: 'Who makes it',
                    body: 'Two paragraphs: the hands, the place, the batch. On a shelf this size the maker is the brand, which is why this band comes before the promises and not after them.',
                    image: photo('3:2', 'maker 3:2'),
                    variant: 'split-reverse',
                },
            },
            {
                type: 'features',
                id: 'promises',
                slots: {
                    kicker: '03 · How it works',
                    headline: 'Shipping, returns, and where it is made',
                    variant: 'icons',
                    columns: '3',
                    items: [
                        { icon: 'map', title: 'Ships from …', text: 'How fast, and what it costs.' },
                        { icon: 'key', title: 'Returns', text: 'The window, in one line.' },
                        { icon: 'star', title: 'Made here', text: 'By whom, and in what size batch.' },
                    ],
                },
            },
            {
                type: 'showcase',
                id: 'collections',
                slots: {
                    kicker: '04 · Collections',
                    headline: 'By collection',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('4:3', 'collection 4:3'),
                            title: 'A collection',
                            text: 'One line on what is in it.',
                            bullets: [],
                            link: 'category:prints',
                            link_label: 'See them all',
                        },
                        {
                            image: photo('4:3', 'collection 4:3'),
                            title: 'Another',
                            text: 'One line.',
                            bullets: [],
                            link: 'category:oils',
                            link_label: 'See them all',
                        },
                        {
                            image: photo('4:3', 'collection 4:3'),
                            title: 'A third',
                            text: 'One line.',
                            bullets: [],
                            link: 'category:gifts',
                            link_label: 'See them all',
                        },
                    ],
                },
            },
            {
                type: 'testimonials',
                id: 'people',
                slots: {
                    kicker: '05 · People',
                    headline: 'What people say',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A name',
                            text: 'Their words.',
                            bullets: ['What they bought'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'Another name',
                            text: 'Their words.',
                            bullets: ['What they bought'],
                        },
                    ],
                },
            },
            {
                type: 'newsletter',
                id: 'stay-in-touch',
                slots: {
                    tone: 'alt',
                    kicker: '06 · Stay in touch',
                    headline: 'New batches, now and then',
                    note: 'One address, no name asked. We write when there is something to say.',
                    submit_label: 'Sign up',
                    variant: 'card',
                },
            },
            { type: 'footer', id: 'footer', slots: { tone: 'night', note: 'Your name — made in …' } },
        ],
        nav: [
            { label: 'The shelf', target: 'section:shelf' },
            { label: 'Collections', target: 'section:collections' },
            { label: 'The maker', target: 'section:the-maker' },
            { label: 'Basket', target: 'cart' },
            { label: 'My orders', target: 'visits' },
        ],
    },
    shopping: [
        {
            where: 'hero',
            ratio: '16:7',
            count: 1,
            note: 'The workbench or the shelf, wide, under the words.',
        },
        {
            where: 'shop',
            ratio: '1:1',
            count: 0,
            note: 'Product photographs go on the items themselves, in the catalog — one square per thing you sell, on the same ground.',
        },
        { where: 'story', ratio: '3:2', count: 1, note: 'The maker, at work.' },
        {
            where: 'showcase',
            ratio: '4:3',
            count: 3,
            note: 'One photograph per collection — the cards go to /c/prints, /c/oils and /c/gifts, so name your categories to match or retarget the cards.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 2,
            note: 'A small portrait per person, or leave them out.',
        },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
/**
 * **Resort** — stays, rentals, tours and a shop on one tenant, re-cut from the
 * multi-offer lodge survey (`docs/design/references/homepage-sequences-verticals.md`
 * §5 and §8); built against Alpine Creek.
 *
 * **What was overturned: three sell bands in a row.** §4.3 ran `showcase` →
 * `sailings` → `services` back to back, which is what **no site in nine
 * does** (§5). The device the lodges actually use is the **cross-sell trio** —
 * Mount Engadine's stay · dine · adventure, three cards that go to three
 * pages — plus the *primary* kind sold on the home; sites without the trio end
 * up selling one kind and linking the rest badly (Woodside's shop and
 * restaurant folded into a "trading post"; the shop is the weakest-placed kind
 * on every one of them). So the trio is band 3 and the `stay` control is band
 * 4, and sailings, services and the shop live on their category pages, which
 * is where the trio's cards point.
 *
 * **Which rows decided each band** (§5 unless said otherwise):
 *  - `hero cover` — 9 of 9.
 *  - `story split` at band 2 — story or an area guide comes before the first
 *    sellable thing on every one of the nine; the first sell is band 4 to 6
 *    everywhere.
 *  - `showcase cards`, the trio, at band 3 — Mount Engadine band 6,
 *    KISC's four activity cards band 4, Togwotee's activities + rentals
 *    band 6. Rentals never get their own band on any site; they ride in the
 *    trio, which is exactly what this card does.
 *  - `stay` at band 4 — hospitality-led sites put rooms first (Sunset House,
 *    Amoray, Playa Nicuesa, Eiger View); adventure-led ones swap bands 3 and 4
 *    and the document is one move away from being either.
 *  - `gallery filmstrip` late — Mount Engadine, Thunder Mountain, Amoray,
 *    Eiger View; the place is the proof and it comes after the offer.
 *  - `testimonials cards` — Mount Engadine, Sunset House, Amoray, two thirds
 *    down.
 *  - `cta` closing — the lodges are the one vertical where the cta is the
 *    closer (§7: 36 % overall, and the only vertical it is strong on).
 *
 * **`hours` is offered, not default.** §4.3 ended this page with the sauna's
 * week; **no sauna or spa hours band appears on any of the nine** (§5), and
 * the survey cannot prove a band nobody runs. A property whose sauna keeps
 * hours ticks it and stands at eight — our differentiator, taken deliberately
 * rather than inherited.
 *
 * The `inquiry` band (a wedding, a retreat, a week) goes the same way: it is
 * §5's "packages and offers" idea, 18 % overall, and §8 lists an `offers`
 * block as one of the two gaps the next variants pass has to fill. Until it
 * exists, the ask lives in the cta and the occasions list lives on a content
 * page.
 */
const RESORT = {
    name: 'resort',
    label: 'Resort',
    vertical: 'Stays, rentals, tours and a shop on one tenant',
    client: 'Alpine Creek',
    blurb: 'The property in a photograph, the valley in a paragraph, three ways to be here as three cards, two dates and a party size for the lodges, the grounds as a strip you scroll, what guests said, and one last ask.',
    document: {
        preset: 'gilded',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'Your valley',
                    headline: 'Your property, in one line',
                    sub: 'One sentence on the place; the lodges, the guided days and the rentals are below.',
                    cta_label: 'Check dates',
                    cta_target: 'section:rooms',
                    variant: 'cover',
                    scrim: 'dusk',
                },
            },
            {
                type: 'story',
                id: 'the-valley',
                slots: {
                    kicker: '01 · The place',
                    headline: 'What this place is',
                    body: 'Three or four sentences: the valley, the building, the seasons, and what a day here looks like when nobody has planned one. The area guide belongs on a page of its own.',
                    image: photo('3:2', 'valley 3:2'),
                    variant: 'split',
                },
            },
            {
                type: 'showcase',
                id: 'the-trio',
                slots: {
                    tone: 'alt',
                    kicker: '02 · Three ways to be here',
                    headline: 'Stay, go out, or borrow the gear',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('4:3', 'lodge 4:3'),
                            title: 'The lodges',
                            text: 'One line on the rooms and cabins.',
                            bullets: ['Sleeps two to …'],
                            link: 'category:lodges',
                            link_label: 'See the lodges',
                        },
                        {
                            image: photo('4:3', 'guided 4:3'),
                            title: 'Guided days',
                            text: 'One line on what you take people out to do.',
                            bullets: ['Half day · full day'],
                            link: 'category:tours',
                            link_label: 'See the days',
                        },
                        {
                            image: photo('4:3', 'rentals 4:3'),
                            title: 'Rentals',
                            text: 'One line on what is in the shed.',
                            bullets: ['By the hour'],
                            link: 'category:rentals',
                            link_label: 'See the gear',
                        },
                    ],
                },
            },
            {
                type: 'stay',
                id: 'rooms',
                slots: {
                    kicker: '03 · Dates',
                    headline: 'The lodges',
                    note: 'Two dates and a party size. Guided days, rentals and the shop are booked from their own pages.',
                    default_nights: 2,
                },
            },
            {
                type: 'gallery',
                id: 'grounds',
                slots: {
                    kicker: '04 · The grounds',
                    headline: 'Around the property',
                    variant: 'filmstrip',
                    images: [
                        { src: photo('3:2', 'grounds 3:2'), alt: 'The creek', caption: '' },
                        { src: photo('3:2', 'grounds 3:2'), alt: 'The ridge', caption: '' },
                        { src: photo('3:2', 'grounds 3:2'), alt: 'The sauna', caption: '' },
                        { src: photo('3:2', 'grounds 3:2'), alt: 'The great room', caption: '' },
                    ],
                },
            },
            {
                type: 'testimonials',
                id: 'guests',
                slots: {
                    kicker: '05 · Guests',
                    headline: 'What guests say',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A guest’s name',
                            text: 'Their words, as they wrote them.',
                            bullets: ['The lodge · the season'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'Another name',
                            text: 'Their words.',
                            bullets: ['The guided day'],
                        },
                        {
                            image: photo('1:1', '1:1'),
                            title: 'A third',
                            text: 'Their words.',
                            bullets: ['The week'],
                        },
                    ],
                },
            },
            {
                type: 'cta',
                id: 'come-up',
                slots: {
                    tone: 'accent',
                    kicker: '06 · Come up',
                    headline: 'A night, a day out, or a week of both?',
                    note: 'Pick your dates; anything bigger — a wedding, a retreat — and write to us instead.',
                    link_label: 'Check dates',
                    link_target: 'section:rooms',
                },
            },
            { type: 'footer', id: 'footer', slots: { tone: 'night', note: 'Your name — since …' } },
        ],
        pages: {
            faq: {
                title: 'Questions',
                sections: [
                    {
                        type: 'faq',
                        id: 'faq',
                        slots: {
                            tone: 'alt',
                            kicker: 'Good questions',
                            headline: 'Good questions',
                            variant: 'list',
                            items: [
                                { q: 'How do I get there?', a: 'Your answer.' },
                                { q: 'What is there to do in the rain?', a: 'Your answer.' },
                                { q: 'Can we book the whole place?', a: 'Your answer.' },
                            ],
                        },
                    },
                ],
            },
        },
        nav: [
            { label: 'Lodges', target: 'category:lodges' },
            { label: 'Guided days', target: 'category:tours' },
            { label: 'Rentals', target: 'category:rentals' },
            { label: 'Dates', target: 'section:rooms' },
            { label: 'Questions', target: 'page:faq' },
            { label: 'My visits', target: 'visits' },
        ],
    },
    shopping: [
        {
            where: 'hero',
            ratio: '16:9',
            count: 1,
            note: 'The property from above or across the valley.',
        },
        { where: 'story', ratio: '3:2', count: 1, note: 'The valley, or the building in its season.' },
        {
            where: 'showcase',
            ratio: '4:3',
            count: 3,
            note: 'One per kind — a lodge, a guided day, the gear shed. The cards go to /c/lodges, /c/tours and /c/rentals, so name your categories to match or retarget the cards.',
        },
        {
            where: 'gallery',
            ratio: '3:2',
            count: 4,
            note: 'The grounds as a strip: creek, ridge, sauna, the great room.',
        },
        {
            where: 'testimonials',
            ratio: '1:1',
            count: 3,
            note: 'A small portrait per guest, or leave them out.',
        },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'Your mark or wordmark, on a transparent ground.',
        },
    ],
};
function segmentPage(copy) {
    return {
        title: copy.title,
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: copy.kicker,
                    kicker_style: 'tracked',
                    headline: `${copy.headline} ${copy.accent}`,
                    headline_accent: copy.accent,
                    sub: copy.sub,
                    cta_label: 'Book a discovery call',
                    // `section:book` from a content page is the home page's booking band
                    // — the hash leaves `/p/<slug>`, the router lands on the home and the
                    // browser anchors. The same target the home's own hero carries, so
                    // the ask is one ask on every page of the site.
                    cta_target: 'section:book',
                    variant: 'minimal',
                },
            },
            {
                type: 'story',
                id: 'the-problem',
                slots: {
                    kicker: '01 · The problem',
                    headline: 'What this actually costs you',
                    body: copy.problem,
                    variant: 'editorial',
                    link_label: 'See what it costs',
                    link_target: 'page:pricing',
                },
            },
            {
                type: 'features',
                id: 'what-it-does',
                slots: {
                    tone: 'alt',
                    kicker: '02 · What it does',
                    headline: 'Six things, in the order you will meet them',
                    variant: 'numbered',
                    columns: '3',
                    items: copy.features,
                },
            },
            {
                type: 'story',
                id: 'closer-look',
                slots: {
                    kicker: '03 · A closer look',
                    headline: 'The two screens that matter',
                    variant: 'zigzag',
                    items: copy.splits.map((split) => ({
                        image: photo('4:3', 'screen 4:3'),
                        title: split.title,
                        text: split.text,
                        bullets: split.bullets,
                    })),
                },
            },
            {
                type: 'faq',
                id: 'questions',
                slots: {
                    tone: 'alt',
                    kicker: '04 · Questions',
                    headline: 'What owners ask first',
                    variant: 'accordion',
                    items: copy.questions,
                },
            },
            {
                type: 'cta',
                id: 'ask',
                slots: {
                    tone: 'accent',
                    kicker: '05 · Next',
                    headline: copy.close.headline,
                    note: copy.close.note,
                    link_label: 'Book a discovery call',
                    link_target: 'section:book',
                },
            },
        ],
    };
}
/**
 * **The Platform** — the site that sells *this* to an operator, built against
 * **clearDay**, our own product site, which `docs/clients.md` lists as client 1
 * (the tenant is `t_jswaves`; ids are never renamed) with no template at all.
 *
 * **Its evidence is a different layer, and that is the whole of why it is
 * exempt from three rules the other ten share.** The three homepage surveys in
 * `docs/design/references/` read **tenant** homepages — a spa selling
 * treatments to a guest — and produced seven bands, the story at band 2 or 3,
 * and a closing band per vertical. This page sells software to the owner of
 * that spa. Its reference is
 * [`timekey.md`](../../../docs/design/references/timekey.md): a direct
 * competitor's platform home, captured 2026-09-18 and read band by band,
 * whose §5.1 says in so many words that our vocabulary already covered its
 * shape except for two blocks — **pricing tiers** and a **timeline** view of
 * `features`. Both landed in `./sections.ts` with this template, and neither
 * was invented for it.
 *
 * **§1's thirteen bands, mapped onto our vocabulary and our own matrix:**
 *
 * | TimeKey | Here | Why |
 * |---|---|---|
 * | 2 hero split, the product as the picture | `hero split` | the two-beat headline is `headline_accent`, which we already had; the picture is the booking card |
 * | 3 the customer's day, five timestamps | `features timeline` | §1 band 3, and the note's own italics: *the story is the CUSTOMER's day, never the company's* |
 * | 5, 6, 8, 10 four alternating feature splits | `story zigzag`, three panels | four splits of one product is the same band four times; three is what we actually sell — the book, the site, the front desk |
 * | 7 segments trio | `showcase cards`, four | four kinds, each going to its own landing page |
 * | 9 four stats | `features stats` | the same dataset drawn as figures (§9.6 decision 2) |
 * | 11 pricing, one marked, fine print under | `pricing` | the new block; three tiers, the middle featured |
 * | 12 faq | **not on the home** | the one band we do not take — three surveys put the FAQ off a home page and §9.4 says so plainly. It lives on `/p/pricing`, under the tiers, where an objection is actually raised |
 * | 13 cta with the risk reversal | `cta split` | the risk reversal is the `note`, one quiet line under the ask |
 * | 14 newsletter | `newsletter card` | the closing band, after the cta, as on every surveyed site |
 * | 4 logos, 0 utility ribbon | dropped | integrations we do not have yet; a ribbon is `notice`, and it is a tenant's offer band, not ours |
 *
 * That is **eight bands** between the masthead and the footer rather than
 * seven, and the extra one is the second `features` block — the stats. Two
 * `features` blocks on one page is exactly what the `stats` variant's own
 * comment anticipates ("a tenant who wants both writes two features blocks");
 * `arrangeSections` keys the second apart and always could.
 *
 * **The `booking` band is the discovery call.** `PINNED_SECTION` mounts the
 * flow whether or not a document arranges it, so placing it at the foot under
 * `id: 'book'` is what makes "Book a discovery call" — in the hero, in every
 * tier, in the closer and on all six pages — one ask with one destination.
 * `card` is the control's fourth view: one panel with the month in it, which
 * is the right shape for the sparse schedule a discovery call actually has
 * (and the shape the reference's own hero card uses, §1 band 2).
 *
 * **The preset is `coastal`**, which is the one of the four built to sit under
 * a screenshot: white ground, near-square corners, a grotesk in both slots and
 * a marine accent (`./presets.ts`). Serene and Gilded are warm paper under a
 * serif — right for a spa, and they make a product capture look pasted on —
 * and Bistro is a printed menu.
 *
 * **Every picture on this template is a real product screenshot, never stock.**
 * The shopping list below is the capture checklist, and it is the one list in
 * this file that a photographer cannot fill: a stock photograph of a laptop on
 * a desk is the thing the reference note singles out as what this cohort never
 * does (`timekey.md` §4: *"every band's image is a product mock with plausible
 * data, never a stock photograph"*). Captures carry plausible names, times and
 * amounts, and honest scarcity — "4 left", "Sold out" — because that is what
 * the product actually shows.
 */
const THE_PLATFORM = {
    name: 'the-platform',
    label: 'The Platform',
    vertical: 'The product site — selling the platform to operators',
    client: 'clearDay — the platform’s own site',
    blurb: 'The booking card as the picture, one owner’s day as five screens, the three pieces as splits, the four kinds as cards, four honest numbers, three plans with the middle one marked, the ask with its risk reversal, the letter, and a discovery call at the foot.',
    document: {
        preset: 'coastal',
        tagline: 'One book, one booking site, one front desk that never sleeps.',
        sections: [
            {
                type: 'hero',
                id: 'hero',
                slots: {
                    kicker: 'For spas, restaurants, stays and tours',
                    kicker_style: 'tracked',
                    headline: 'One book behind the counter. One front desk that never sleeps.',
                    headline_accent: 'One front desk that never sleeps.',
                    sub: 'Take the booking on your own site, answer the telephone at two in the morning, and close out once at the end of the day.',
                    cta_label: 'Book a discovery call',
                    cta_target: 'section:book',
                    variant: 'split',
                    image: photo('4:3', 'booking card 4:3'),
                },
            },
            {
                /*
                 * §1 band 3. Five moments, each a real screen: the label is the
                 * timestamp, the title is what happened, the text is what it saved,
                 * and the image is the screen it happened on. The register is the
                 * owner's own — "you", the counter, the island — and the sentence is
                 * always the outcome, never the feature.
                 */
                type: 'features',
                id: 'a-day',
                slots: {
                    kicker: '01 · A Tuesday',
                    headline: 'One ordinary day, from before you open to after you close',
                    note: 'Nothing below is a feature list. It is the day the five screens beside it belong to.',
                    variant: 'timeline',
                    items: [
                        {
                            label: '06:20',
                            title: 'Tomorrow is already laid out',
                            text: 'You open the day on your phone before you unlock the door: eleven bookings, two deposits outstanding, one therapist out. It is the same book the website and the telephone were writing into all night.',
                            image: photo('16:10', 'day screen 16:10'),
                        },
                        {
                            label: '09:15',
                            title: 'A cancellation fills itself',
                            text: 'The nine-thirty cancels. The next name on the list is offered the hour, takes it and pays the deposit, and the gap closes before you have finished the coffee.',
                            image: photo('16:10', 'day screen 16:10'),
                        },
                        {
                            label: '13:40',
                            title: 'The lunch rush never reaches the counter',
                            text: 'Four calls come in while both hands are busy. Three are bookings the agent takes in the caller’s own language; the fourth is a question it would not guess at, and it waits for you as a task rather than as a voicemail.',
                            image: photo('16:10', 'day screen 16:10'),
                        },
                        {
                            label: '17:05',
                            title: 'Tomorrow confirms itself',
                            text: 'Everyone booked for tomorrow gets the reminder, in the language they first wrote to you in. The two who do not answer are the two worth a telephone call.',
                            image: photo('16:10', 'day screen 16:10'),
                        },
                        {
                            label: '21:30',
                            title: 'The money agrees with the book',
                            text: 'Close out once. Cash, card and gift credit land on the same day’s takings, and the discount is the list price less what you charged. No spreadsheet, no evening.',
                            image: photo('16:10', 'day screen 16:10'),
                        },
                    ],
                },
            },
            {
                /*
                 * §1 bands 5, 6, 8 and 10 are four alternating feature splits of one
                 * product. Four splits is the same band four times; three is what we
                 * actually sell, and the zigzag is the view that alternates them.
                 */
                type: 'story',
                id: 'the-three',
                slots: {
                    kicker: '02 · What you get',
                    headline: 'Three pieces, and they are the same book',
                    variant: 'zigzag',
                    items: [
                        {
                            image: photo('4:3', 'screen 4:3'),
                            title: 'The book',
                            text: 'One grid behind the counter for rooms, tables, beds and seats. The rule that stops two people holding one thing lives in the database, so a double booking is impossible rather than unlikely.',
                            bullets: [
                                'One scheduling engine, read by every surface',
                                'Works on the iPad you already own, and when the island loses the link',
                                'A conflict becomes a task for the owner, never a quiet merge',
                            ],
                        },
                        {
                            image: photo('4:3', 'screen 4:3'),
                            title: 'The booking site',
                            text: 'Your own address, your own photographs, a page you arrange rather than one we edit for you. It takes the booking itself — no third-party widget, no second calendar to keep in step.',
                            bullets: [
                                'Eleven shapes, four moods, your words',
                                'Deposits, gift credit and the basket, on your own domain',
                                'Published in an afternoon, not commissioned as a project',
                            ],
                        },
                        {
                            image: photo('4:3', 'screen 4:3'),
                            title: 'The AI front desk',
                            text: 'Voice, chat and messages answered out of the same book the counter reads. It offers only what is genuinely free, holds it while the guest decides, and hands over the moment it is out of its depth.',
                            bullets: [
                                'Answers at two in the morning, in the guest’s language',
                                'Never invents an hour the book does not have',
                                'Hands to a person with the whole conversation attached',
                            ],
                        },
                    ],
                },
            },
            {
                /*
                 * §1 band 7, the segments trio — four here, one per kind, each going to
                 * the landing page that reads the shared anatomy in that kind's own
                 * economics (§2).
                 */
                type: 'showcase',
                id: 'the-kinds',
                slots: {
                    tone: 'alt',
                    kicker: '03 · Who it is for',
                    headline: 'Four kinds of business, one book',
                    variant: 'cards',
                    items: [
                        {
                            image: photo('4:3', 'kind 4:3'),
                            title: 'Spas and salons',
                            text: 'A room sells time, and empty time is gone for good.',
                            bullets: ['The person as a choice', 'Deposits that end no-shows'],
                            link: 'page:for-spas',
                            link_label: 'For spas and salons',
                        },
                        {
                            image: photo('4:3', 'kind 4:3'),
                            title: 'Restaurants',
                            text: 'Ordering first, covers second — in that order, because that is where the money is.',
                            bullets: ['The menu with a basket', 'Split shifts, one week'],
                            link: 'page:for-restaurants',
                            link_label: 'For restaurants',
                        },
                        {
                            image: photo('4:3', 'kind 4:3'),
                            title: 'Stays and rentals',
                            text: 'Two dates, a party size, and a rate that is right for those nights.',
                            bullets: ['Server-priced quotes', 'Direct, not through a channel'],
                            link: 'page:for-stays',
                            link_label: 'For stays and rentals',
                        },
                        {
                            image: photo('4:3', 'kind 4:3'),
                            title: 'Tours and charters',
                            text: 'A board of real departures with real seats left, which nobody else ships.',
                            bullets: ['Seats, not enquiries', 'Weather days without telephone calls'],
                            link: 'page:for-tours',
                            link_label: 'For tours and charters',
                        },
                    ],
                },
            },
            {
                /*
                 * §1 band 9. Four figures and a provenance sentence — and the figures
                 * here are **placeholders that say what kind of number belongs in
                 * them**, because the honest version of this band on a young product is
                 * a number you can point at. The note under it is the provenance line.
                 */
                type: 'features',
                id: 'numbers',
                slots: {
                    kicker: '04 · The numbers',
                    headline: 'Four figures, and where each comes from',
                    note: 'Replace each with the real one and say where it came from. A number nobody can trace is worth less than no number at all.',
                    variant: 'stats',
                    columns: '4',
                    items: [
                        {
                            title: '00 000',
                            text: 'Bookings taken through the platform last month. Count them out of the book, for a named month.',
                        },
                        {
                            title: '00 %',
                            text: 'Of those bookings that arrived outside opening hours — the front desk’s whole case, in one figure.',
                        },
                        {
                            title: '0.0 s',
                            text: 'Median time to answer a guest, measured at the door rather than in the log.',
                        },
                        {
                            title: '00',
                            text: 'Businesses running on it today. When it is two, say two — specificity is what a young product has instead of a logo wall.',
                        },
                    ],
                },
            },
            {
                /*
                 * §1 band 11: three tiers, the middle one marked, the fine print
                 * underneath. Authored words, every one of them — no tier names a
                 * catalog item and nothing here prices a booking (see `pricing` in
                 * `./sections.ts`).
                 *
                 * **The two numbers are the proposed rate card, verbatim**
                 * (`docs/business/operating-model.md` §1: yourstore core $79/mo,
                 * aiconcierge base $149/mo, bespoke build quoted). That is why the
                 * middle tier's period says *on top of Counter* rather than reading as
                 * $149 for both products — a placeholder that quietly halved the
                 * combined price would be the one kind of copy on this page that is
                 * checkable and wrong. The same three tiers appear on `/p/pricing`, and
                 * a change here is a change there.
                 */
                type: 'pricing',
                id: 'plans',
                slots: {
                    tone: 'alt',
                    kicker: '05 · Pricing',
                    headline: 'Three ways to pay for it',
                    note: 'Per location, monthly, no setup fee. The middle one is what most businesses end up on.',
                    items: [
                        {
                            name: 'Counter',
                            price: '$79',
                            period: 'per month',
                            note: 'One location, one book, your own booking site. Everything a single room needs and nothing it does not.',
                            bullets: [
                                'The book, the staff app and the counter',
                                'Your booking site on your own domain',
                                'Deposits, gift credit and the shelf',
                                'Reminders by email and text',
                            ],
                            link_label: 'Start here',
                            link_target: 'section:book',
                        },
                        {
                            name: 'Front desk',
                            price: '$149',
                            period: 'per month, on top of Counter',
                            featured: true,
                            note: 'The agent answering the telephone, the chat and the messages while you work — added to Counter rather than instead of it.',
                            bullets: [
                                'Added to Counter, not in place of it',
                                'Voice, chat and messages, around the clock',
                                'Two languages to begin with, more on request',
                                'Slots held and deposits taken on the call',
                                'Every conversation on the guest’s own record',
                            ],
                            link_label: 'Book a discovery call',
                            link_target: 'section:book',
                        },
                        {
                            name: 'Group',
                            price: 'Custom',
                            period: 'per location, billed yearly',
                            note: 'Several locations on one book, your own history imported, and a person who knows your business by name.',
                            bullets: [
                                'Counter and Front desk, per location',
                                'Import from whatever you run today, reconciled to the cent',
                                'One catalog, local prices',
                                'A named person and a response time in writing',
                            ],
                            link_label: 'Talk to us',
                            link_target: 'section:book',
                        },
                    ],
                    fine_print: 'Thirty days on the whole platform, no card, cancel in a click. We take no commission on a booking you took yourself; the front desk’s calls are billed at cost by the minute and itemized on the invoice. Prices are in US dollars and exclude local tax.',
                },
            },
            {
                type: 'cta',
                id: 'ready',
                slots: {
                    tone: 'accent',
                    kicker: '06 · Ready',
                    headline: 'Ready before your next season is',
                    // §1 band 13's risk reversal, as one quiet line under the ask —
                    // `timekey.md` §5.2 makes it a copy rule for every template here.
                    note: 'Thirty days on the whole platform, no card, and your data leaves with you if it turns out not to fit.',
                    link_label: 'Book a discovery call',
                    link_target: 'section:book',
                    variant: 'split',
                },
            },
            {
                type: 'newsletter',
                id: 'letter',
                slots: {
                    kicker: '07 · Keep in touch',
                    headline: 'One letter a month',
                    note: 'What we shipped, what we got wrong, and one thing another owner worked out. No launches.',
                    submit_label: 'Send it to me',
                    consent: 'One email a month about the platform. Unsubscribe from the bottom of any of them.',
                    thanks: 'Thank you — the next one goes out at the start of the month.',
                    variant: 'card',
                },
            },
            {
                /*
                 * The ask, and the anchor every "Book a discovery call" on the site
                 * points at.
                 *
                 * **`card`, because a discovery call is a sparse schedule.** The
                 * studied control's week strip is right for a business that opens
                 * every day and sells by the hour; thirty-minute calls on the days
                 * somebody is free are a handful of openings scattered across a month,
                 * and a strip of seven days would show a guest six greyed squares and
                 * ask them to page. The card puts the whole month up with a mark on
                 * every day, which is the control the reference this template was read
                 * from uses for exactly the same reason (`timekey.md` §1 band 2).
                 */
                type: 'booking',
                id: 'book',
                slots: {
                    headline: 'Book a discovery call',
                    note: 'Thirty minutes, screen shared, your own catalog on the table. Pick a time that suits.',
                    variant: 'card',
                },
            },
            {
                type: 'footer',
                id: 'footer',
                slots: {
                    tone: 'night',
                    note: 'clearDay — St. Thomas, US Virgin Islands.',
                    rows: [
                        { label: 'Product', value: 'The book · The booking site · The front desk' },
                        { label: 'Kinds', value: 'Spas · Restaurants · Stays · Tours' },
                        { label: 'Company', value: 'Pricing · Contact · Status' },
                    ],
                },
            },
        ],
        pages: {
            'for-spas': segmentPage({
                title: 'For spas and salons',
                kicker: 'Spas and salons',
                headline: 'A room booked',
                accent: 'to the last hour.',
                sub: 'The treatment, the person and the deposit, taken on your own page while you have both hands busy.',
                problem: 'A spa sells time in a room, and empty time is gone for good. An hour nobody booked on Tuesday cannot be sold again on Wednesday, so every quiet hour is a permanent loss and every no-show is two: the hour that went and the guest you turned away for it.\n\nThe answer is not a louder telephone. It is a book that is right at three in the morning, a page that takes the deposit before the hour is held, and a reminder in the language the guest wrote to you in — so the hour that was sold is the hour that is kept.',
                features: [
                    {
                        title: 'The person as a choice',
                        text: 'Anyone first, then each therapist by name. A guest who does not mind gets the fullest room; a guest who minds gets who they asked for.',
                    },
                    {
                        title: 'Deposits that end no-shows',
                        text: 'Take part of the price when the hour is held. The window, the amount and the refund rule are yours, and they are enforced by the book rather than by a conversation.',
                    },
                    {
                        title: 'A menu guests can read',
                        text: 'Treatments as cards with the duration and what the price includes, and a Book on every row that opens that treatment with the hour already chosen.',
                    },
                    {
                        title: 'Gift cards that reconcile',
                        text: 'Sell credit, redeem it against any treatment, and see it in the day’s takings as gift credit rather than as cash that never arrived.',
                    },
                    {
                        title: 'The shelf, on the same page',
                        text: 'Retail beside the treatments, in one basket, settled at the same counter. A guest who loved the oil buys the oil.',
                    },
                    {
                        title: 'One week, kept once',
                        text: 'Your opening hours, holidays and split shifts live in one place, and the site, the agent and the counter all read it. The page can never advertise an hour the book will refuse.',
                    },
                ],
                splits: [
                    {
                        title: 'No-shows, quietly designed out',
                        text: 'Four moments do the work, and none of them is a telephone call: the hold, the day before, the morning of, and the one that did not come.',
                        bullets: [
                            'At booking — the deposit, and the cancellation window in plain words',
                            'Twenty-four hours — the reminder, with one tap to move it',
                            'Two hours — the last nudge, only to those who have not replied',
                            'After — the hour is released and the next name on the list is offered it',
                        ],
                    },
                    {
                        title: 'What the guest sees',
                        text: 'The treatment, then the person — Anyone first — then the hours that are genuinely free, with the deposit stated before anything is held.',
                        bullets: [
                            'Real availability, not an enquiry form',
                            'The deposit and the window before the card, never after',
                            'Their own visits and receipts, without an account to create',
                        ],
                    },
                ],
                questions: [
                    {
                        q: 'We already have a booking page. Does this replace it?',
                        a: 'Yes, and it takes your catalog, your history and your gift-card balances with it. We reconcile the import to the cent before you switch, and you see the reconciliation.',
                    },
                    {
                        q: 'What happens when the island loses the link?',
                        a: 'The counter keeps working from its own copy and catches up when the link comes back. A conflict becomes a task for you rather than a merge nobody asked for.',
                    },
                    {
                        q: 'Can two therapists share one room?',
                        a: 'Yes. A room, a chair and a therapist are each a resource, and the book will not let two bookings hold the same one at the same time.',
                    },
                    {
                        q: 'Do we have to take deposits?',
                        a: 'No. They are per treatment, and you can start without them and turn them on for the ones that get cancelled.',
                    },
                    {
                        q: 'How long does it take to be live?',
                        a: 'An afternoon for the site and the catalog. The import and the first week beside your old system take longer, and we do them with you.',
                    },
                ],
                close: {
                    headline: 'See it with your own treatments in it',
                    note: 'Thirty minutes, your catalog on the table, and no card until you have seen the book work.',
                },
            }),
            'for-restaurants': segmentPage({
                title: 'For restaurants',
                kicker: 'Restaurants',
                headline: 'Orders taken',
                accent: 'while the kitchen cooks.',
                sub: 'Your menu with a basket on your own page, and a front desk that answers the four calls nobody could pick up.',
                problem: 'A restaurant’s telephone rings hardest at exactly the moment nobody can answer it. Every unanswered call is an order that went somewhere else, and every order taken through a delivery app is the same food sold at a third less.\n\nSo the first thing this does is not reservations. It is ordering — your menu, your prices, your page, your basket — and a voice that picks up on the second ring at seven on a Friday, takes the order into the same kitchen ticket, and tells the guest when it will be ready.',
                features: [
                    {
                        title: 'The menu as the shop',
                        text: 'Categories, dishes, options and modifiers, with a basket on the page. The order is for pick-up and the time is chosen in the basket, not on the telephone.',
                    },
                    {
                        title: 'A voice on the second ring',
                        text: 'The front desk answers in English or Spanish, reads the same menu, prices the order the way the server does, and never invents a dish.',
                    },
                    {
                        title: 'Split shifts, one week',
                        text: 'Lunch and dinner with the afternoon closed is one week in one place, and the page can never advertise a service the kitchen is not running.',
                    },
                    {
                        title: 'Tickets that read like tickets',
                        text: 'An order lands as a kitchen ticket with the modifiers where a cook expects them, not as an email somebody has to retype.',
                    },
                    {
                        title: 'Tables, when you want them',
                        text: 'Covers are a band you can add: party, date and time, with the room’s real capacity behind it. Ordering stays the front door.',
                    },
                    {
                        title: 'One close-out',
                        text: 'Cash, card and gift credit land on the same day’s takings, and a discount is the list price less what you charged. The evening ends when service does.',
                    },
                ],
                splits: [
                    {
                        title: 'The calls you never picked up',
                        text: 'Four rings during service is not a staffing problem you can solve with staff. The agent takes the ones that are orders and leaves you the ones that are not.',
                        bullets: [
                            'Answers every call, in the caller’s own language',
                            'Quotes only what the kitchen is actually serving now',
                            'Takes payment or holds the order for the counter',
                            'Hands a real question to a person, with the conversation attached',
                        ],
                    },
                    {
                        title: 'What the guest sees',
                        text: 'A page that looks like your room, a menu that looks like your menu, and a basket that says when the food will be ready before it asks for a card.',
                        bullets: [
                            'Your photographs and your words, on your own domain',
                            'The ready time before the payment, never after',
                            'A text when it goes in the bag',
                        ],
                    },
                ],
                questions: [
                    {
                        q: 'Do we have to stop using the delivery apps?',
                        a: 'No. Most kitchens keep them and move their regulars here, because the same order is worth a third more when it comes through your own page.',
                    },
                    {
                        q: 'Can the agent take a reservation as well as an order?',
                        a: 'Yes, when you turn covers on. It reads the same room and the same week, so it cannot seat a table that is not there.',
                    },
                    {
                        q: 'What about modifiers — no onions, extra sauce?',
                        a: 'They are part of the dish in the catalog, so they price correctly, print on the ticket, and the agent knows about them.',
                    },
                    {
                        q: 'Who owns the guest list?',
                        a: 'You do. Guests are per business, never a shared account across ours, and the list leaves with you if you go.',
                    },
                    {
                        q: 'How does the money reconcile?',
                        a: 'Revenue lands on the day the money moved, cash and gift credit separately, and the import from your old system is reconciled to the cent before cutover.',
                    },
                ],
                close: {
                    headline: 'Hear it answer your own telephone',
                    note: 'Thirty minutes, your menu on the table, and no card until you have heard it take an order.',
                },
            }),
            'for-stays': segmentPage({
                title: 'For stays and rentals',
                kicker: 'Stays and rentals',
                headline: 'Direct bookings',
                accent: 'you do not pay for twice.',
                sub: 'Two dates, a party size and a rate that is right for those nights — on your own address, with no commission on top.',
                problem: 'A room sells one night at a time, and the nights you did not sell are not stock you carry forward. Meanwhile the channel that did sell them takes fifteen per cent of the ones you did — on a guest who typed your name into a search box and could have arrived on your own page.\n\nWhat brings them to your page is not a lower price. It is a page that answers the only question they have — are those two nights free, and what do they cost — before asking for anything, and a front desk that answers it at midnight when the channel’s chat widget does not.',
                features: [
                    {
                        title: 'Dates and guests, answered',
                        text: 'Two dates and a party size return the room types that are genuinely free, with a rate priced by the server for exactly those nights.',
                    },
                    {
                        title: 'Rates that are never stale',
                        text: 'A nightly rate is a function of the dates asked about, so the page never publishes a number that is right for some nights and wrong for others.',
                    },
                    {
                        title: 'A room per page',
                        text: 'Each room type gets its own page with its own photographs, what is included and what it sleeps — the page a guest actually shares.',
                    },
                    {
                        title: 'Deposits and the window',
                        text: 'Take the first night or a percentage, state the cancellation window in plain words, and let the book enforce both.',
                    },
                    {
                        title: 'Answered at midnight',
                        text: 'The front desk quotes real availability, holds a room while the guest talks it over, and hands a real question to you in the morning.',
                    },
                    {
                        title: 'The front desk is not a week',
                        text: 'A property that is open at three in the morning does not print opening hours. The site knows the difference.',
                    },
                ],
                splits: [
                    {
                        title: 'The commission, designed out',
                        text: 'Nothing here fights the channels. It just makes the direct path the easier one, for the guest who was going to come anyway.',
                        bullets: [
                            'Your own domain, your own photographs, no widget',
                            'The same nights, without fifteen per cent on top',
                            'The guest’s details are yours, not a channel’s',
                            'A returning guest books in two taps',
                        ],
                    },
                    {
                        title: 'What the guest sees',
                        text: 'Dates, guests, and what is actually free — then the total for those nights, with the deposit and the window stated before the card.',
                        bullets: [
                            'Real availability, not a request form',
                            'The total for those nights, not a from-price',
                            'Their booking and receipts, with no account to create',
                        ],
                    },
                ],
                questions: [
                    {
                        q: 'Do we have to leave the channels?',
                        a: 'No. Most properties keep them and move their repeat guests here, because a direct night is worth what it says it is worth.',
                    },
                    {
                        q: 'Can we take a whole-property booking?',
                        a: 'Yes, and the book will not let the individual rooms be sold underneath it.',
                    },
                    {
                        q: 'What about cleaning gaps between stays?',
                        a: 'A turnaround is a resource the room holds, so a same-day arrival cannot be booked into a room that is not ready.',
                    },
                    {
                        q: 'Do you handle rentals as well as rooms?',
                        a: 'Yes — a kayak by the hour and a cabin by the night are the same kind of thing to the book, with different units.',
                    },
                    {
                        q: 'How long does it take to be live?',
                        a: 'An afternoon for the site and the rooms. The rates, the import and the first fortnight beside your old system take longer, and we do them with you.',
                    },
                ],
                close: {
                    headline: 'See your own rooms answer in real time',
                    note: 'Thirty minutes, your rates on the table, and no card until you have seen the quote come back right.',
                },
            }),
            'for-tours': segmentPage({
                title: 'For tours and charters',
                kicker: 'Tours and charters',
                headline: 'Departures that',
                accent: 'fill themselves.',
                sub: 'A board of real dates with real seats left, and a front desk that answers the weather question before you are back on the dock.',
                problem: 'A departure is the most perishable thing a small business sells. The boat leaves at nine whether it has twelve people on it or four, and the four are the whole difference between a good season and a season you got through.\n\nWhat fills the empty seats is not a longer enquiry form. It is a board that shows the actual dates with the actual seats left, a deposit taken while the guest is still reading, and somebody answering the weather question at seven in the evening — which is when it is asked and when nobody is at a desk.',
                features: [
                    {
                        title: 'The board, with real seats',
                        text: 'What is leaving, on which day, with how many places left. Nobody else in this market ships it; every other site is an enquiry form.',
                    },
                    {
                        title: 'Seats, not enquiries',
                        text: 'A departure holds a real capacity, so twelve seats is twelve seats and the thirteenth booking is refused by the book rather than by an apology.',
                    },
                    {
                        title: 'Weather days without calls',
                        text: 'Cancel a departure once and everyone on it is told, offered the next one, and refunded to the rail they paid on.',
                    },
                    {
                        title: 'Private charters too',
                        text: 'A private booking takes the whole boat, and the board stops selling seats on it the moment it does.',
                    },
                    {
                        title: 'Answered at seven in the evening',
                        text: 'The front desk knows which departures still have room, what the swell rule is, and when to fetch you instead of guessing.',
                    },
                    {
                        title: 'The manifest, on the dock',
                        text: 'Who is coming, what they paid, and who has not shown — on the phone in your hand, working with no signal.',
                    },
                ],
                splits: [
                    {
                        title: 'The empty seat, designed out',
                        text: 'Four moments fill a boat, and none of them is a follow-up email somebody has to remember to send.',
                        bullets: [
                            'The board shows scarcity honestly — “4 left”, and “Sold out” when it is',
                            'A deposit holds the seat while the guest talks it over',
                            'A cancellation offers the seat to the next name on the list',
                            'A weather day moves everyone to the next departure in one action',
                        ],
                    },
                    {
                        title: 'What the guest sees',
                        text: 'The dates that are actually running, the seats actually left, and the total before the card — not a form that promises somebody will be in touch.',
                        bullets: [
                            'Real departures, real counts, no enquiry form',
                            'The deposit and the weather policy before the card',
                            'Their ticket and their photographs afterwards',
                        ],
                    },
                ],
                questions: [
                    {
                        q: 'Can we run two boats on the same morning?',
                        a: 'Yes. Each boat is a resource with its own capacity, and the board shows both departures with their own counts.',
                    },
                    {
                        q: 'What happens on a weather day?',
                        a: 'You cancel the departure once. Everyone on it is told, offered the alternatives you name, and refunded to the rail they paid on.',
                    },
                    {
                        q: 'Do you take partial payments?',
                        a: 'Yes — a deposit to hold the seat and the balance on the day, or the whole thing up front. It is per departure kind.',
                    },
                    {
                        q: 'Does it work on the dock with no signal?',
                        a: 'The manifest works from its own copy and catches up when the signal comes back. A conflict becomes a task for you, never a silent merge.',
                    },
                    {
                        q: 'Can the agent sell a seat?',
                        a: 'Yes, out of the same board, and it will not sell the thirteenth seat on a twelve-seat boat.',
                    },
                ],
                close: {
                    headline: 'See your own season on a board',
                    note: 'Thirty minutes, your departures on the table, and no card until you have seen the seats count down.',
                },
            }),
            concierge: {
                title: 'The AI front desk',
                sections: [
                    {
                        type: 'hero',
                        id: 'hero',
                        slots: {
                            kicker: 'The AI front desk',
                            kicker_style: 'tracked',
                            headline: 'Someone answers. Even at two in the morning.',
                            headline_accent: 'Even at two in the morning.',
                            sub: 'Voice, chat and messages, out of the same book the counter reads — and a hand-over the moment it is out of its depth.',
                            cta_label: 'Book a discovery call',
                            cta_target: 'section:book',
                            variant: 'minimal',
                        },
                    },
                    {
                        type: 'story',
                        id: 'how-it-works',
                        slots: {
                            kicker: '01 · How it works',
                            headline: 'It reads your book. It does not imagine one.',
                            variant: 'zigzag',
                            items: [
                                {
                                    image: photo('4:3', 'screen 4:3'),
                                    title: 'It only offers what is free',
                                    text: 'Every hour it quotes comes from the same availability the counter sees. There is one scheduling engine on this platform, and the agent is not allowed a second one.',
                                    bullets: [
                                        'Real availability, asked live',
                                        'Holds the slot while the guest decides',
                                        'Refuses politely rather than inventing an hour',
                                    ],
                                },
                                {
                                    image: photo('4:3', 'screen 4:3'),
                                    title: 'It knows when to fetch you',
                                    text: 'A complaint, an unusual request or a question it has no grounds to answer becomes a task with the whole conversation attached — not a dropped call and not a confident guess.',
                                    bullets: [
                                        'Hands over with the transcript and the guest’s record',
                                        'Never negotiates a price it was not given',
                                        'Everything it did is on the guest’s own record',
                                    ],
                                },
                            ],
                        },
                    },
                    {
                        type: 'features',
                        id: 'what-it-handles',
                        slots: {
                            tone: 'alt',
                            kicker: '02 · What it handles',
                            headline: 'Six things it takes off the counter',
                            variant: 'numbered',
                            columns: '3',
                            items: [
                                {
                                    title: 'The telephone at seven on a Friday',
                                    text: 'It picks up on the second ring while both of your hands are busy, and it does not put anybody on hold.',
                                },
                                {
                                    title: 'The two in the morning question',
                                    text: 'Half of all booking questions arrive outside opening hours. They stop being tomorrow’s callbacks.',
                                },
                                {
                                    title: 'The guest’s own language',
                                    text: 'It answers in the language it was written to in, and the reminder goes out in the same one.',
                                },
                                {
                                    title: 'Recognising a regular',
                                    text: 'It knows the guest by the number they are calling from, within your business only — there is no account shared across ours.',
                                },
                                {
                                    title: 'Taking the deposit',
                                    text: 'It can take payment on the call, on your own rail, and the booking is held the moment it clears.',
                                },
                                {
                                    title: 'Knowing when to stop',
                                    text: 'Out of scope is a hand-over, not an improvisation. That line is the reason it can be trusted with a telephone.',
                                },
                            ],
                        },
                    },
                    {
                        type: 'faq',
                        id: 'questions',
                        slots: {
                            kicker: '03 · Questions',
                            headline: 'What owners ask first',
                            variant: 'accordion',
                            items: [
                                {
                                    q: 'Will it sound like a robot?',
                                    a: 'It sounds like a calm receptionist with a short script and no hold music. The discovery call is the honest way to judge that — you hear it answer your own number.',
                                },
                                {
                                    q: 'Can it make a booking we cannot honour?',
                                    a: 'No. It asks the same engine the counter asks, and the database refuses two bookings on one resource regardless of who made them.',
                                },
                                {
                                    q: 'What does it do when it does not know?',
                                    a: 'It says so and hands over, with the conversation attached. It is not permitted to answer outside what your catalog and your book say.',
                                },
                                {
                                    q: 'Do we have to use it?',
                                    a: 'No. The book and the booking site stand on their own; the front desk is the tier above them.',
                                },
                                {
                                    q: 'What does it cost to run?',
                                    a: 'The tier covers it, and the calls are billed through at cost by the minute and itemized. You can see the minutes before you commit to them.',
                                },
                            ],
                        },
                    },
                    {
                        type: 'cta',
                        id: 'ask',
                        slots: {
                            tone: 'accent',
                            kicker: '04 · Next',
                            headline: 'Hear it answer your own telephone',
                            note: 'Thirty minutes, no card, and your data leaves with you if it turns out not to fit.',
                            link_label: 'Book a discovery call',
                            link_target: 'section:book',
                        },
                    },
                ],
            },
            pricing: {
                title: 'Pricing',
                sections: [
                    {
                        type: 'pricing',
                        id: 'plans',
                        slots: {
                            kicker: 'Pricing',
                            headline: 'Three ways to pay for it',
                            note: 'Per location, monthly, no setup fee. The middle one is what most businesses end up on.',
                            items: [
                                {
                                    name: 'Counter',
                                    price: '$79',
                                    period: 'per month',
                                    note: 'One location, one book, your own booking site. Everything a single room needs and nothing it does not.',
                                    bullets: [
                                        'The book, the staff app and the counter',
                                        'Your booking site on your own domain',
                                        'Deposits, gift credit and the shelf',
                                        'Reminders by email and text',
                                    ],
                                    link_label: 'Start here',
                                    link_target: 'section:book',
                                },
                                {
                                    name: 'Front desk',
                                    price: '$149',
                                    period: 'per month, on top of Counter',
                                    featured: true,
                                    note: 'The agent answering the telephone, the chat and the messages while you work — added to Counter rather than instead of it.',
                                    bullets: [
                                        'Added to Counter, not in place of it',
                                        'Voice, chat and messages, around the clock',
                                        'Two languages to begin with, more on request',
                                        'Slots held and deposits taken on the call',
                                        'Every conversation on the guest’s own record',
                                    ],
                                    link_label: 'Book a discovery call',
                                    link_target: 'section:book',
                                },
                                {
                                    name: 'Group',
                                    price: 'Custom',
                                    period: 'per location, billed yearly',
                                    note: 'Several locations on one book, your own history imported, and a person who knows your business by name.',
                                    bullets: [
                                        'Counter and Front desk, per location',
                                        'Import from whatever you run today, reconciled to the cent',
                                        'One catalog, local prices',
                                        'A named person and a response time in writing',
                                    ],
                                    link_label: 'Talk to us',
                                    link_target: 'section:book',
                                },
                            ],
                            fine_print: 'Thirty days on the whole platform, no card, cancel in a click. We take no commission on a booking you took yourself; the front desk’s calls are billed at cost by the minute and itemized on the invoice. Prices are in US dollars and exclude local tax.',
                        },
                    },
                    {
                        /*
                         * **This is where the FAQ went, and it is the one band of §1 we do
                         * not take onto the home.** TimeKey puts seven objections at band
                         * 12; our three homepage surveys put the FAQ on 10 % of real homes
                         * and §9.4 says plainly that it is not a home band. A pricing
                         * objection is raised under the price, so the questions sit here,
                         * under the tiers, where the reader already has one.
                         */
                        type: 'faq',
                        id: 'questions',
                        slots: {
                            tone: 'alt',
                            kicker: 'Before you ask',
                            headline: 'The seven that come up',
                            variant: 'accordion',
                            items: [
                                {
                                    q: 'Is there a setup fee?',
                                    a: 'No. We set the first version up with you, and that is part of the first month rather than a line on an invoice.',
                                },
                                {
                                    q: 'Do you take a commission on bookings?',
                                    a: 'Not on a booking you took yourself. The only usage billing is the front desk’s call minutes, passed through at cost and itemized.',
                                },
                                {
                                    q: 'What happens to our existing bookings and history?',
                                    a: 'They come with you. We import them and reconcile the money to the cent before you switch, and you see the reconciliation.',
                                },
                                {
                                    q: 'Can we start on one tier and move?',
                                    a: 'Yes, in either direction, at the start of a month. Nothing is re-implemented when you do.',
                                },
                                {
                                    q: 'What if we have more than one location?',
                                    a: 'Group is per location on one book, with one catalog and local prices. Talk to us before you buy two of anything.',
                                },
                                {
                                    q: 'Who owns the data?',
                                    a: 'You do, and it leaves with you in a form you can read. Guests belong to your business and are never shared across ours.',
                                },
                                {
                                    q: 'What is the trial actually?',
                                    a: 'Thirty days on the whole platform with no card. If you stop, nothing is charged and your export is waiting for you.',
                                },
                            ],
                        },
                    },
                    {
                        type: 'cta',
                        id: 'ask',
                        slots: {
                            tone: 'accent',
                            kicker: 'Next',
                            headline: 'Still the fastest way to decide: see it with your own catalog in it',
                            note: 'Thirty days on the whole platform, no card, and your data leaves with you if it turns out not to fit.',
                            link_label: 'Book a discovery call',
                            link_target: 'section:book',
                        },
                    },
                ],
            },
        },
        /*
         * Six entries, one short of the cap, and **`visits` is not one of them**.
         * On a tenant's site the seventh entry is "My visits", because the person
         * reading it is a guest with a booking; on a product site the reader is an
         * owner deciding, and the seventh thing they want is the price. The shell
         * keeps the standing link to `visits` regardless — a built-in may be named
         * and placed but never removed (`./pages.ts`, `BUILTIN_PATHS`) — so
         * dropping it here moves it out of the bar rather than out of the site.
         */
        nav: [
            { label: 'Spas', target: 'page:for-spas' },
            { label: 'Restaurants', target: 'page:for-restaurants' },
            { label: 'Stays', target: 'page:for-stays' },
            { label: 'Tours', target: 'page:for-tours' },
            { label: 'Concierge', target: 'page:concierge' },
            { label: 'Pricing', target: 'page:pricing' },
        ],
    },
    /*
     * **The capture list, not a photo shoot.** Every entry is a screenshot of
     * the real product with real-looking data in it — plausible names, times and
     * amounts, and honest scarcity ("4 left", "Sold out"). A stock photograph of
     * a laptop is the one thing this template must never ship: `timekey.md` §4
     * records that every band's image on the competitor's site is a product mock
     * and never a stock photograph, and that is the whole of why the site reads
     * as a product rather than as a brochure.
     */
    shopping: [
        {
            where: 'hero',
            ratio: '4:3',
            count: 1,
            note: 'The booking card, live: a month with availability dots, time chips with seats left, one button, and the confirmation line under it. This is the picture the whole page rests on.',
        },
        {
            where: 'features',
            ratio: '16:10',
            count: 5,
            note: 'The five day-screens, in order: tomorrow’s book at 06:20, the waiting-list offer at 09:15, the agent’s call transcript at 13:40, the reminder batch at 17:05, the close-out at 21:30.',
        },
        {
            where: 'story',
            ratio: '4:3',
            count: 3,
            note: 'The three splits on the home: the book’s day grid, a dressed tenant booking site, and a front-desk conversation ending in a held slot.',
        },
        {
            where: 'showcase',
            ratio: '4:3',
            count: 4,
            note: 'One per kind — a spa menu, a restaurant basket, a dates-and-guests quote, a departures board with seats left. The four cards go to /p/for-spas, /p/for-restaurants, /p/for-stays and /p/for-tours.',
        },
        {
            where: 'story',
            ratio: '4:3',
            count: 10,
            note: 'Two screens per landing page — the pain designed out, then the guest’s own side of the booking — for spas, restaurants, stays, tours and the concierge page.',
        },
        {
            where: 'logo',
            ratio: 'any',
            count: 1,
            note: 'The clearDay mark, on a transparent ground.',
        },
    ],
};
export const TEMPLATES = {
    'one-room': ONE_ROOM,
    'the-practice': THE_PRACTICE,
    'the-studio': THE_STUDIO,
    'the-quiet-spa': THE_QUIET_SPA,
    'the-venue': THE_VENUE,
    'the-table': THE_TABLE,
    stays: STAYS,
    departures: DEPARTURES,
    'the-shelf': THE_SHELF,
    resort: RESORT,
    'the-platform': THE_PLATFORM,
};
export const TEMPLATE_LIST = TEMPLATE_NAMES.map((n) => TEMPLATES[n]);
//# sourceMappingURL=templates.js.map